/* ==========================================================================
   Brazos Site Supply v4 for Shopify. One vanilla script, deferred on every
   page. One global: window.BZ (window.BSS belongs to the analytics snippet).

   TRUST
   Prices, weights and ladders come only from JSON the server rendered from
   the Shopify variant objects and snippets/bz-data-ladders.liquid:
     #bz-variant-map  every page   {"<id>": [price_cents, grams, [[min_qty, cents], ...], ladder group]}
     #bz-pdp-data     product      the product's variants
     #bz-catalog      project      every variant with names, SKU and color
   The project list in localStorage holds ONLY {variant_id, qty}. Nothing
   else from storage or the URL is ever used to price, weigh or link.

   Unit price = the lowest rung with min_qty <= qty, else the variant price.
   Weight: grams are summed first, then converted to lb and rounded once.
   A variant with 0 grams has no published weight: shown as unknown, never 0.

   Modules below run only when their root element is on the page:
     core        header menu, Planning disclosure, search, cart count and bar,
                 card color dots, dialogs, checkout verdict
     collection  [data-catalog]   product families, grid or list view, filters,
                                  sort, chips, compare, drawer
     product     [data-pdp]       gallery, options, model links, quantity pricing,
                                  Add to Cart, Buy Now, Get a Project Quote
     project     #bz-quote-form   cart lines, Check Out, freight estimate,
                                  validation, one quote post

   The cart and the project quote are ONE list (localStorage, ids and
   quantities only). Online checkout goes through a Shopify cart permalink
   /cart/<variant>:<qty>,...; Shopify automatic discounts apply the same
   quantity ladders at checkout, counting every unit on one ladder together
   (2026-09-25 plan v1). Each cart shows ONE way out: Check Out when checkout
   can take the whole list, otherwise the project quote with the reason.
   ========================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'bz-project-v1';
  var GRAMS_PER_LB = 453.59237;
  var ASSET_BASE = window.BZ_ASSET_BASE || '';

  document.documentElement.classList.add('js');

  /* -- formatting -------------------------------------------------------- */

  var wholeFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  var centsFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  var numFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

  /* cents -> "$14,425" (whole dollars) or "$1,099.50" */
  function money(cents) {
    var n = Number(cents);
    if (!isFinite(n)) { return ''; }
    n = Math.round(n);
    return n % 100 === 0 ? wholeFmt.format(n / 100) : centsFmt.format(n / 100);
  }
  function moneyCents(cents) {
    var n = Number(cents);
    if (!isFinite(n)) { return ''; }
    return centsFmt.format(Math.round(n) / 100);
  }
  function lbText(lb) { return numFmt.format(lb) + ' lb'; }
  /* grams -> whole pounds, or null when the weight is not published */
  function gramsToLb(grams) {
    var g = Number(grams);
    if (!isFinite(g) || g <= 0) { return null; }
    return Math.round(g / GRAMS_PER_LB);
  }
  /* v4.9.4 fix: grams -> pounds for the freight bands and the online weight limit, rounded UP
     to 0.01 lb. Shopify checkout compares the exact weight (bands to 150, 150.01-350,
     350.01-700), so 350.28 lb is in the 700 lb band. Whole pounds are for display only. */
  function gramsToLbExact(grams) {
    var g = Number(grams);
    if (!isFinite(g) || g <= 0) { return null; }
    return Math.ceil(g / GRAMS_PER_LB * 100 - 1e-6) / 100;
  }
  /* display pounds: whole pounds, unless rounding would hide that the weight is over a limit */
  var lbDecFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  function lbShow(exact, limits) {
    if (exact === null || exact === undefined) { return ''; }
    var r = Math.round(exact);
    for (var i = 0; i < (limits || []).length; i += 1) {
      var e = Number(limits[i]);
      if (e && r <= e && exact > e) { return lbDecFmt.format(exact) + ' lb'; }
    }
    return lbText(r);
  }
  function bandLimits(fr, maxLb) {
    var out = maxLb ? [maxLb] : [];
    ((fr && fr.zones) || []).forEach(function (z) { (z.bands || []).forEach(function (b) { out.push(Number(b.max_lb)); }); });
    return out;
  }

  /* ladder: [[min_qty, unit_price_cents], ...] sorted by min_qty */
  function unitPrice(ladder, baseCents, qty) {
    var price = Number(baseCents) || 0;
    var q = Number(qty) || 0;
    if (!ladder || !ladder.length) { return price; }
    for (var i = 0; i < ladder.length; i += 1) {
      if (q >= Number(ladder[i][0])) { price = Number(ladder[i][1]); }
    }
    return price;
  }

  /* whole numbers 1 to 999 only; anything else is null */
  function parseQty(raw) {
    var text = String(raw === undefined || raw === null ? '' : raw).trim();
    if (!/^[0-9]+$/.test(text)) { return null; }
    var n = Number(text);
    if (!isFinite(n) || n < 1 || n > 999) { return null; }
    return n;
  }

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) { return null; }
    try { return JSON.parse(el.textContent); } catch (err) { return null; }
  }

  function esc(s) {
    return String(s === null || s === undefined ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function assetUrl(file) { return ASSET_BASE + file; }

  /* -- event bus --------------------------------------------------------- */

  var listeners = {};
  var bus = {
    on: function (name, fn) { (listeners[name] = listeners[name] || []).push(fn); },
    emit: function (name, detail) {
      var list = (listeners[name] || []).slice();
      for (var i = 0; i < list.length; i += 1) {
        try { list[i](detail); } catch (err) { if (window.console) { console.error(err); } }
      }
    }
  };

  /* -- trusted variant map ---------------------------------------------- */

  var variantMap = readJSON('bz-variant-map') || {};
  function trusted(id) {
    var rec = variantMap[String(id)];
    if (!rec) { return null; }
    return { price: Number(rec[0]) || 0, grams: Number(rec[1]) || 0, ladder: rec[2] || [], group: rec[3] || '' };
  }

  /* -- project store: [{variant_id, qty}] only ---------------------------- */

  var cache = null;

  function readStorage() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      if (!parsed || !parsed.length) { return []; }
      var clean = [];
      var seen = {};
      for (var i = 0; i < parsed.length; i += 1) {
        var item = parsed[i] || {};
        var id = String(item.variant_id || '').replace(/[^0-9]/g, '');
        var q = parseQty(item.qty);
        if (!id || !q || seen[id]) { continue; }
        seen[id] = true;
        clean.push({ variant_id: id, qty: q });
      }
      return clean;
    } catch (err) {
      return [];
    }
  }

  function writeStorage(list) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); return true; } catch (err) { return false; }
  }

  function items() { if (cache === null) { cache = readStorage(); } return cache; }

  function changed() {
    writeStorage(cache);
    bus.emit('project:change', { items: cache.slice() });
  }

  function indexOf(id) {
    var list = items();
    for (var i = 0; i < list.length; i += 1) { if (list[i].variant_id === String(id)) { return i; } }
    return -1;
  }

  function find(id) { var i = indexOf(id); return i > -1 ? items()[i] : null; }

  function insert(item, index) {
    var list = items();
    var id = String(item.variant_id);
    var q = parseQty(item.qty) || 1;
    var at = indexOf(id);
    if (at > -1) {
      list[at].qty = Math.min(999, list[at].qty + q);
    } else {
      var pos = index === undefined || index === null ? list.length : Math.max(0, Math.min(list.length, Math.round(Number(index))));
      list.splice(pos, 0, { variant_id: id, qty: q });
    }
    changed();
  }

  function setQty(id, qty) {
    var at = indexOf(id);
    if (at < 0) { return false; }
    var q = Math.round(Number(qty) || 0);
    if (q <= 0) { items().splice(at, 1); } else { items()[at].qty = Math.min(999, q); }
    changed();
    return true;
  }

  /* drop ids the server catalog does not carry; returns how many were dropped */
  function prune(isKnown) {
    var list = items();
    var kept = [];
    for (var i = 0; i < list.length; i += 1) { if (isKnown(list[i].variant_id)) { kept.push(list[i]); } }
    var dropped = list.length - kept.length;
    if (dropped) { cache = kept; changed(); }
    return dropped;
  }

  /* priced from the trusted map; unknown ids are left out of every figure */
  function summary() {
    var list = items();
    var t = { lines: 0, units: 0, cents: 0, grams: 0, unknownWeight: false };
    var pool = {};
    for (var j = 0; j < list.length; j += 1) {
      var pr = trusted(list[j].variant_id);
      if (pr && pr.group) { pool[pr.group] = (pool[pr.group] || 0) + list[j].qty; }
    }
    for (var i = 0; i < list.length; i += 1) {
      var rec = trusted(list[i].variant_id);
      if (!rec) { continue; }
      t.lines += 1;
      t.units += list[i].qty;
      t.cents += unitPrice(rec.ladder, rec.price, rec.group ? pool[rec.group] : list[i].qty) * list[i].qty;
      if (rec.grams > 0) { t.grams += rec.grams * list[i].qty; } else { t.unknownWeight = true; }
    }
    return t;
  }

  /* Online checkout verdict for a list of priced lines
     [{id, qty, grams, base, unit, x, title}]: Shopify prices the lines (quantity
     ladders run as automatic discounts) and rates freight from its table, which
     stops at maxLb. */
  /* Analytics for the custom pixel (Shopify.analytics.publish). No name, email, phone or ZIP. */
  function currencyCode() { return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'; }
  function publish(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') { window.Shopify.analytics.publish(name, payload); }
    } catch (err) { /* analytics never blocks */ }
  }

  function checkoutVerdict(list, maxLb, dest) {
    var v = { ok: false, reason: '', url: '', listCents: 0, quoteCents: 0, volume: false, lb: 0 };
    if (!list.length) { v.reason = 'empty'; return v; }
    var grams = 0, unknown = false, blocked = null, crated = false;
    list.forEach(function (l) {
      v.listCents += l.base * l.qty;
      v.quoteCents += l.unit * l.qty;
      if (l.unit < l.base) { v.volume = true; }
      if (l.x && !blocked) { blocked = l; }
      if (l.parcel) { return; }
      crated = true;
      if (l.grams > 0) { grams += l.grams * l.qty; } else { unknown = true; }
    });
    v.exactLb = gramsToLbExact(grams);
    if (!blocked && crated && dest && dest.status === 'unmapped') {
      v.lb = gramsToLb(grams);
      v.reason = 'Online checkout ships crated freight to the lower 48 states and DC. For ' + (dest.state || 'ZIP ' + dest.zip) + ', send this list as a project quote.';
      return v;
    }
    v.lb = gramsToLb(grams);
    if (blocked) {
      v.reason = blocked.x === 2
        ? 'The ' + blocked.title + ' is made to order, so this list goes by project quote, where the lead time is confirmed. A person replies within one business day.'
        : 'The ' + blocked.title + ' ships in a 98 in crate that needs an extreme-length freight quote, so it is sold by project quote.';
      return v;
    }
    if (unknown) { v.reason = 'One item has no published crated weight, so its freight has to be quoted.'; return v; }
    if (maxLb && v.exactLb > maxLb) {
      v.reason = 'At ' + lbShow(v.exactLb, [maxLb]) + ' this shipment is above the ' + lbText(maxLb) + ' our online freight table covers. Send it as a project quote and a person replies within one business day.';
      return v;
    }
    v.ok = true;
    v.url = '/cart/' + list.map(function (l) { return encodeURIComponent(l.id) + ':' + l.qty; }).join(',');
    return v;
  }

  var store = {
    items: items, find: find, insert: insert, add: function (item) { insert(item); },
    setQty: setQty, remove: function (id) { return setQty(id, 0); },
    clear: function () { cache = []; changed(); }, prune: prune, summary: summary,
    reload: function () { cache = readStorage(); }
  };

  /* -- dialogs ------------------------------------------------------------ */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var openDialogs = [];

  function setBackgroundInert(keepEl, on) {
    var children = document.body.children;
    for (var i = 0; i < children.length; i += 1) {
      var node = children[i];
      if (node === keepEl || node.tagName === 'SCRIPT') { continue; }
      if (on) { node.setAttribute('inert', ''); node.setAttribute('aria-hidden', 'true'); }
      else { node.removeAttribute('inert'); node.removeAttribute('aria-hidden'); }
    }
  }

  function openDialog(el, opener) {
    if (!el) { return; }
    if (el.parentNode !== document.body) { document.body.appendChild(el); }
    el.hidden = false;
    setBackgroundInert(el, true);
    document.documentElement.style.overflow = 'hidden';
    openDialogs.push({ el: el, opener: opener || document.activeElement });
    var first = el.querySelector('[data-dialog-initial]') || el.querySelector(FOCUSABLE);
    if (first) { first.focus(); } else { el.setAttribute('tabindex', '-1'); el.focus(); }
  }

  function closeDialog(el) {
    var record = null;
    for (var i = openDialogs.length - 1; i >= 0; i -= 1) {
      if (!el || openDialogs[i].el === el) { record = openDialogs.splice(i, 1)[0]; break; }
    }
    if (!record) { return; }
    record.el.hidden = true;
    setBackgroundInert(record.el, false);
    if (!openDialogs.length) { document.documentElement.style.overflow = ''; }
    if (record.opener && document.contains(record.opener)) { record.opener.focus(); }
  }

  document.addEventListener('keydown', function (event) {
    if (!openDialogs.length) { return; }
    var top = openDialogs[openDialogs.length - 1];
    if (event.key === 'Escape') { event.preventDefault(); closeDialog(top.el); return; }
    if (event.key !== 'Tab') { return; }
    var list = [].slice.call(top.el.querySelectorAll(FOCUSABLE)).filter(function (n) { return n.offsetParent !== null; });
    if (!list.length) { return; }
    if (event.shiftKey && document.activeElement === list[0]) { event.preventDefault(); list[list.length - 1].focus(); }
    else if (!event.shiftKey && document.activeElement === list[list.length - 1]) { event.preventDefault(); list[0].focus(); }
  });

  document.addEventListener('click', function (event) {
    var closer = event.target.closest ? event.target.closest('[data-dialog-close]') : null;
    if (closer) { event.preventDefault(); closeDialog(closer.closest('.dialog')); return; }
    if (event.target.classList && event.target.classList.contains('dialog')) { closeDialog(event.target); }
  });

  /* -- header count and project bar -------------------------------------- */

  function renderChrome() {
    var t = summary();
    var badges = document.querySelectorAll('[data-bz-count-badge]');
    for (var i = 0; i < badges.length; i += 1) { badges[i].textContent = String(t.units); badges[i].hidden = t.units === 0; }
    var named = document.querySelectorAll('[data-bz-count-name]');
    for (var b = 0; b < named.length; b += 1) {
      if (t.units === 0) { named[b].removeAttribute('aria-label'); }
      else { named[b].setAttribute('aria-label', named[b].getAttribute('data-bz-count-name') + ', ' + t.units + (t.units === 1 ? ' item' : ' items')); }
    }
    var sentence = t.units === 0 ? 'Your project list is empty.'
      : t.units + (t.units === 1 ? ' item' : ' items') + ' in your cart, ' + money(t.cents) + ' before freight and tax.';
    var says = document.querySelectorAll('[data-bz-count-text]');
    for (var s = 0; s < says.length; s += 1) { says[s].textContent = sentence; }

    var bar = document.querySelector('[data-bz-bar]');
    if (bar) {
      var onProject = document.body.getAttribute('data-page') === 'project';
      var show = t.units > 0 && !onProject;
      bar.hidden = !show;
      document.body.classList.toggle('has-project-bar', show);
      if (show) {
        var countEl = bar.querySelector('[data-bz-bar-count]');
        var subEl = bar.querySelector('[data-bz-bar-subtotal]');
        if (countEl) { countEl.textContent = t.units + (t.units === 1 ? ' item' : ' items') + (t.lines > 1 ? ' on ' + t.lines + ' lines' : ''); }
        if (subEl) { subEl.textContent = money(t.cents); }
      }
    }
  }

  /* -- header: menu, Planning, search ------------------------------------ */

  function initMenu() {
    var toggle = document.querySelector('[data-bz-menu-toggle]');
    var nav = document.getElementById('site-nav');
    var header = document.querySelector('[data-site-header]');
    if (!toggle || !nav || !header) { return; }
    /* Shopify wraps each section in its own div: keep that body child live */
    var keep = header.closest('body > *') || header;
    function collapsed() { return window.getComputedStyle(toggle).display !== 'none'; }
    function setOpen(open) {
      if (open) { nav.style.setProperty('--nav-top', Math.max(0, Math.round(header.getBoundingClientRect().bottom)) + 'px'); }
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open ? 'Close' : 'Menu';
      document.documentElement.style.overflow = open ? 'hidden' : '';
      var children = document.body.children;
      for (var i = 0; i < children.length; i += 1) {
        var node = children[i];
        if (node === keep || node.tagName === 'SCRIPT') { continue; }
        if (open && collapsed()) { node.setAttribute('inert', ''); } else { node.removeAttribute('inert'); }
      }
    }
    toggle.addEventListener('click', function () { setOpen(toggle.getAttribute('aria-expanded') !== 'true'); });
    window.addEventListener('resize', function () {
      if (toggle.getAttribute('aria-expanded') === 'true' && !collapsed()) { setOpen(false); }
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { setOpen(false); toggle.focus(); }
    });
  }

  function initPlanningMenu() {
    [].slice.call(document.querySelectorAll('[data-nav-menu]')).forEach(function (wrap) {
      var button = wrap.querySelector('button');
      var panel = wrap.querySelector('.nav-menu__panel');
      if (!button || !panel) { return; }
      function set(open) { button.setAttribute('aria-expanded', open ? 'true' : 'false'); panel.hidden = !open; }
      button.addEventListener('click', function () { set(button.getAttribute('aria-expanded') !== 'true'); });
      wrap.addEventListener('focusout', function (event) { if (event.relatedTarget && !wrap.contains(event.relatedTarget)) { set(false); } });
      document.addEventListener('click', function (event) { if (!wrap.contains(event.target)) { set(false); } });
      wrap.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') { event.stopPropagation(); set(false); button.focus(); }
      });
    });
  }

  function initSearch() {
    var toggle = document.querySelector('[data-search-toggle]');
    var panel = document.getElementById('header-search');
    if (!toggle || !panel) { return; }
    var input = panel.querySelector('input');
    function set(open) { toggle.setAttribute('aria-expanded', open ? 'true' : 'false'); panel.hidden = !open; if (open && input) { input.focus(); } }
    toggle.addEventListener('click', function () { set(toggle.getAttribute('aria-expanded') !== 'true'); });
    panel.addEventListener('keydown', function (event) { if (event.key === 'Escape') { set(false); toggle.focus(); } });
  }

  function markCurrentNav() {
    var here = window.location.pathname.replace(/\/$/, '');
    [].slice.call(document.querySelectorAll('.site-nav .nav-link, .site-nav .nav-menu__panel a')).forEach(function (a) {
      var path = (a.getAttribute('href') || '').split('?')[0].split('#')[0].replace(/\/$/, '');
      if (path && path === here) { a.setAttribute('aria-current', 'page'); }
    });
  }

  /* -- product cards: color dots swap the card image --------------------- */

  function cardSwap(dot) {
    var card = dot.closest('.fam-cell') || dot.closest('.product-card');
    if (!card) { return; }
    var img = card.querySelector('.tile img');
    if (!img) { return; }
    var data = null;
    try { data = JSON.parse(dot.getAttribute('data-view')); } catch (err) { data = null; }
    if (!data || !data.src) { return; }
    img.setAttribute('srcset', data.srcset || '');
    img.setAttribute('src', data.src);
    img.setAttribute('alt', data.alt || '');
    var group = dot.closest('.dots') || card;
    [].slice.call(group.querySelectorAll('.dot')).forEach(function (d) { d.setAttribute('aria-pressed', d === dot ? 'true' : 'false'); });
    /* the card link opens the product in the color just chosen */
    var cardLink = card.querySelector('[data-card-link]') || card.querySelector('.product-card__link');
    var chosen = dot.getAttribute('data-color');
    if (cardLink && chosen && !dot.closest('.dots--row')) {
      try {
        var u = new URL(cardLink.getAttribute('href'), window.location.origin);
        u.searchParams.set('color', chosen);
        cardLink.setAttribute('href', u.pathname + u.search);
      } catch (err) { /* keep the link as it is */ }
    }
  }

  function initCardDots() {
    function pick(event) { return event.target.closest ? event.target.closest('.dot[data-view]') : null; }
    document.addEventListener('mouseover', function (event) { var d = pick(event); if (d) { cardSwap(d); } });
    document.addEventListener('focusin', function (event) { var d = pick(event); if (d) { cardSwap(d); } });
    document.addEventListener('click', function (event) { var d = pick(event); if (d) { event.preventDefault(); cardSwap(d); } });
  }

  function ready(fn) {
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', fn); } else { fn(); }
  }

  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) { return; }
    cache = readStorage();
    bus.emit('project:change', { items: cache.slice() });
  });

  bus.on('project:change', renderChrome);

  /* project baskets (campground and bulk pricing pages): add every line, open the cart */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest('[data-basket-add]') : null;
    if (!btn) { return; }
    event.preventDefault();
    String(btn.getAttribute('data-basket-add') || '').split(',').forEach(function (pair) {
      var p = pair.split(':');
      var id = String(p[0] || '').replace(/[^0-9]/g, '');
      var q = parseQty(p[1]);
      if (id && q) { store.add({ variant_id: id, qty: q }); }
    });
    window.location.href = btn.getAttribute('data-cart-url') || '/pages/project-quote';
  });

  window.BZ = {
    version: 1, money: money, moneyCents: moneyCents, gramsToLb: gramsToLb, unitPrice: unitPrice,
    parseQty: parseQty, store: store, bus: bus, dialog: { open: openDialog, close: closeDialog },
    ready: ready, cardSwap: cardSwap, trusted: trusted, checkoutVerdict: checkoutVerdict
  };


  /* ======================================================================
     COLLECTION: product families, grid or list view, sidebar filters,
     search, sort, chips, compare, drawer.
     Each [data-cell] is a family; each [data-row] inside it is one model
     (one Shopify product). Filters test models: a family shows when any of
     its models matches, its card switches to the first matching model, and
     the list view hides the rows that do not match. Sorting re-appends the
     same family nodes; compare builds a table from each row's data-facts.
     State lives in the query string (?mat=&mount=&color=&q=&sort=&layout=
     &compare=&panel=); the layout choice is also remembered per viewer.
     Grid/list is ?layout=, never ?view=: Shopify reads ?view= as an alternate
     template, so ?view=list would drop a collection's template suffix (v4.9
     expanded-metal / in-ground pages). A legacy ?view=list|grid is still read;
     any other ?view= (an alternate template) is kept as it is.
     ====================================================================== */

  function initCollection() {
    var catalog = document.querySelector('[data-catalog]');
    if (!catalog) { return; }

    var grid = catalog.querySelector('[data-grid]');
    var cells = [].slice.call(grid.querySelectorAll('[data-cell]'));
    var rows = [].slice.call(grid.querySelectorAll('[data-row]'));
    var countEl = catalog.querySelector('[data-count]');
    var chipsEl = catalog.querySelector('[data-chips]');
    var emptyEl = catalog.querySelector('[data-empty]');
    var searchEl = catalog.querySelector('[data-search]');
    var sortEl = catalog.querySelector('[data-sort]');
    var drawer = catalog.querySelector('[data-filters]');
    var openBtn = catalog.querySelector('[data-drawer-open]');
    var applyBtn = catalog.querySelector('[data-apply-count]');
    var filterN = catalog.querySelector('[data-filter-n]');
    var boxes = [].slice.call(catalog.querySelectorAll('input[data-filter]'));
    var viewBtns = [].slice.call(catalog.querySelectorAll('[data-view-btn]'));
    var closeBand = document.querySelector('[data-close-band]');

    var tray = catalog.querySelector('[data-tray]');
    var trayItems = tray.querySelector('[data-tray-items]');
    var trayHint = tray.querySelector('[data-tray-hint]');
    var trayClear = tray.querySelector('[data-tray-clear]');
    var trayCompare = tray.querySelector('[data-tray-compare]');
    var region = document.querySelector('[data-compare-region]');
    var regionTable = region.querySelector('[data-compare-table]');
    var regionTitle = region.querySelector('#compare-title');
    var regionBack = region.querySelector('[data-compare-back]');
    var checkboxes = [].slice.call(catalog.querySelectorAll('[data-compare]'));

    var VIEW_KEY = 'bz-collection-view';
    var CATEGORY_NOUN = { tables: 'picnic tables', benches: 'benches', receptacles: 'receptacles', accessories: 'accessories' };
    var ROWS = {
      tables: [['Size', 'size'], ['Overall length', 'length'], ['Seating', 'seats'], ['ADA model', 'ada'], ['Material', 'material'],
        ['Mounting', 'mounting'], ['Finish colors', 'colors'], ['Crated weight', 'weight'], ['Unit price', 'price'], ['Quantity pricing', 'tiers']],
      benches: [['Size', 'size'], ['Overall length', 'length'], ['Seating', 'seats'], ['Backrest', 'backrest'], ['Material', 'material'],
        ['Mounting', 'mounting'], ['Finish colors', 'colors'], ['Crated weight', 'weight'], ['Unit price', 'price'], ['Quantity pricing', 'tiers']],
      receptacles: [['Capacity', 'size'], ['Height', 'height'], ['Material', 'material'], ['Lid options', 'lid'], ['Door', 'door'],
        ['Mounting', 'mounting'], ['Finish colors', 'colors'], ['Crated weight', 'weight'], ['Unit price', 'price'], ['Quantity pricing', 'tiers']],
      accessories: [['Fits', 'fits'], ['Choices', 'choices'], ['Crated weight', 'weight'], ['Unit price', 'price'], ['Quantity pricing', 'tiers']]
    };
    var KINDS = ['cat', 'mat', 'mount', 'ada', 'size', 'color', 'price'];

    var facts = {}, categoryOf = {}, nameOf = {}, searchIndex = {};
    function squash(v) { return v.replace(/[^a-z0-9]+/g, ''); }
    rows.forEach(function (row) {
      var h = row.getAttribute('data-handle');
      var cell = row.closest('[data-cell]');
      categoryOf[h] = row.getAttribute('data-category');
      nameOf[h] = row.getAttribute('data-name');
      try { facts[h] = JSON.parse(row.getAttribute('data-facts')); } catch (err) { facts[h] = null; }
      var text = [row.getAttribute('data-name') || '', row.getAttribute('data-type') || '', row.getAttribute('data-model') || '',
        row.getAttribute('data-config') || '', cell ? cell.getAttribute('data-fname') || '' : '', h, h.replace(/-/g, ' ')].join(' ').toLowerCase();
      searchIndex[h] = { text: text, flat: squash(text) };
    });

    var fams = cells.map(function (cell, i) {
      return {
        cell: cell, order: i, name: cell.getAttribute('data-fname') || '',
        rows: [].slice.call(cell.querySelectorAll('[data-row]')),
        link: cell.querySelector('[data-card-link]'),
        img: cell.querySelector('[data-card] .tile img'),
        price: cell.querySelector('[data-card-price]'),
        dots: cell.querySelector('[data-card-dots]'),
        match: cell.querySelector('[data-card-match]'),
        shown: null, shownColor: null, min: 0, visible: true
      };
    });

    var state = { q: '', sort: '', compare: [], panel: '', view: 'grid' };
    KINDS.forEach(function (k) { state[k] = []; });
    var searchTimer = null;

    function inList(list, v) { return list.indexOf(v) > -1; }
    function dropFrom(list, v) { var at = list.indexOf(v); if (at > -1) { list.splice(at, 1); } }
    function activeCount() { var n = state.q ? 1 : 0; KINDS.forEach(function (k) { n += state[k].length; }); return n; }
    function labelFor(kind, value) {
      for (var i = 0; i < boxes.length; i += 1) {
        if (boxes[i].getAttribute('data-filter') === kind && boxes[i].value === value) { return boxes[i].getAttribute('data-label') || value; }
      }
      return value;
    }
    function storedView() {
      try { var v = window.localStorage.getItem(VIEW_KEY); return v === 'list' ? 'list' : 'grid'; } catch (err) { return 'grid'; }
    }

    function readURL() {
      var params = new URLSearchParams(window.location.search);
      KINDS.forEach(function (k) {
        var raw = (params.get(k) || '').split(',').filter(Boolean);
        state[k] = raw.filter(function (v) { return boxes.some(function (b) { return b.getAttribute('data-filter') === k && b.value === v; }); });
      });
      state.q = params.get('q') || '';
      state.sort = params.get('sort') || '';
      state.panel = params.get('panel') || '';
      var v = params.get('layout') || params.get('view');
      state.view = v === 'list' || v === 'grid' ? v : storedView();
      state.compare = [];
      (params.get('compare') || '').split(',').filter(Boolean).forEach(function (h) {
        if (!categoryOf[h] || inList(state.compare, h)) { return; }
        if (state.compare.length && categoryOf[state.compare[0]] !== categoryOf[h]) { return; }
        if (state.compare.length >= 4) { return; }
        state.compare.push(h);
      });
    }

    function writeURL() {
      var params = new URLSearchParams();
      var tpl = new URLSearchParams(window.location.search).get('view');
      if (tpl && tpl !== 'list' && tpl !== 'grid') { params.set('view', tpl); }
      KINDS.forEach(function (k) { if (state[k].length) { params.set(k, state[k].join(',')); } });
      if (state.q) { params.set('q', state.q); }
      if (state.sort) { params.set('sort', state.sort); }
      if (state.view === 'list') { params.set('layout', 'list'); }
      if (state.compare.length) { params.set('compare', state.compare.join(',')); }
      if (state.panel) { params.set('panel', state.panel); }
      var query = params.toString();
      try { window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : '')); } catch (err) { /* ignore */ }
    }

    function matches(row) {
      if (state.cat.length && !inList(state.cat, row.getAttribute('data-category'))) { return false; }
      if (state.mat.length && !inList(state.mat, row.getAttribute('data-material'))) { return false; }
      if (state.mount.length) {
        var mounts = (row.getAttribute('data-mounting') || '').split(' ');
        if (!state.mount.some(function (m) { return inList(mounts, m); })) { return false; }
      }
      if (state.ada.length && row.getAttribute('data-ada') !== '1') { return false; }
      if (state.size.length && !inList(state.size, row.getAttribute('data-size'))) { return false; }
      if (state.color.length) {
        var colors = (row.getAttribute('data-colors') || '').split(',');
        if (!state.color.some(function (c) { return inList(colors, c); })) { return false; }
      }
      if (state.price.length && !inList(state.price, row.getAttribute('data-pricekey'))) { return false; }
      if (state.q) {
        var index = searchIndex[row.getAttribute('data-handle')];
        var needle = state.q.toLowerCase();
        if (index.text.indexOf(needle) === -1) {
          var flat = squash(needle);
          if (!flat || index.flat.indexOf(flat) === -1) { return false; }
        }
      }
      return true;
    }

    function priceOf(row) { return parseFloat(row.getAttribute('data-price')) || 0; }

    /* the family card shows its first matching model: image, link, dots, price */
    function showModel(f, row, matching) {
      var h = row.getAttribute('data-handle');
      var color = state.color.length === 1 && inList((row.getAttribute('data-colors') || '').split(','), state.color[0]) ? state.color[0] : null;
      /* with only the In-ground filter on, a model that has both mounts shows its in-ground view */
      var groundView = state.mount.length === 1 && state.mount[0] === 'in-ground' ? row.getAttribute('data-view-ground') : null;
      var shownKey = h + (groundView ? '|in-ground' : '');
      if (f.shown !== shownKey) {
        f.shown = shownKey;
        f.shownColor = null;
        var view = null;
        try { view = JSON.parse(groundView || row.getAttribute('data-view') || 'null'); } catch (err) { view = null; }
        if (f.img && view && view.src) {
          f.img.setAttribute('srcset', view.srcset || '');
          f.img.setAttribute('src', view.src);
          f.img.setAttribute('alt', view.alt || '');
          if (view.w) { f.img.setAttribute('width', view.w); f.img.setAttribute('height', view.h); }
        }
        if (f.dots) {
          var src = row.querySelector('.dots--row');
          f.dots.innerHTML = src ? src.innerHTML : '';
          [].slice.call(f.dots.querySelectorAll('.sr-only')).forEach(function (sr) {
            sr.textContent = sr.textContent.replace(/ of the .* model$/, '');
          });
        }
      }
      if (f.link) {
        var url = row.getAttribute('data-url') || f.link.getAttribute('href');
        var cardParams = new URLSearchParams();
        if (color) { cardParams.set('color', color); }
        var rowMounts = (row.getAttribute('data-mounting') || '').split(' ');
        var fixedMount = row.getAttribute('data-mount-fixed');
        if (fixedMount) { cardParams.set('mount', fixedMount); }
        else if (state.mount.length === 1 && rowMounts.length > 1) { cardParams.set('mount', state.mount[0] === 'in-ground' ? 'In-Ground' : 'Portable'); }
        var cq = cardParams.toString();
        f.link.setAttribute('href', url + (cq ? '?' + cq : ''));
      }
      if (color && f.shownColor !== color && f.dots) {
        var dot = f.dots.querySelector('.dot[data-view][data-color="' + color + '"]');
        if (dot) { cardSwap(dot); }
        f.shownColor = color;
      }
      /* price: the lowest matching model; "From" when models or variants differ */
      var min = Infinity, varies = false;
      matching.forEach(function (r) {
        var p = priceOf(r);
        if (min !== Infinity && p !== min) { varies = true; }
        if (p < min) { min = p; }
        if (r.getAttribute('data-varies') === '1') { varies = true; }
      });
      f.min = min === Infinity ? 0 : min;
      if (f.price) {
        var quote = matching.length > 0 && matching.every(function (r) { return r.getAttribute('data-quote') === '1'; });
        f.price.innerHTML = (varies ? 'From ' : '') + '<strong class="num">' + money(Math.round(f.min * 100)) + '</strong> <span class="u-muted">each' + (quote ? ', by project quote' : '') + '</span>';
      }
      if (f.match) {
        f.match.textContent = matching.length < f.rows.length && activeCount() ? matching.length + ' of ' + f.rows.length + ' models match' : '';
      }
    }

    function sortCells() {
      var sorted = fams.slice();
      if (state.sort === 'price-asc' || state.sort === 'price-desc') {
        var dir = state.sort === 'price-asc' ? 1 : -1;
        sorted.sort(function (x, y) { return (x.min - y.min) * dir || x.order - y.order; });
      } else if (state.sort === 'name') {
        sorted.sort(function (x, y) { return x.name.localeCompare(y.name); });
      } else {
        sorted.sort(function (x, y) { return x.order - y.order; });
      }
      var frag = document.createDocumentFragment();
      sorted.forEach(function (f) { frag.appendChild(f.cell); });
      grid.appendChild(frag);
    }

    function renderChips() {
      while (chipsEl.firstChild) { chipsEl.removeChild(chipsEl.firstChild); }
      var entries = [];
      KINDS.forEach(function (k) { state[k].forEach(function (v) { entries.push([k, v, labelFor(k, v)]); }); });
      if (state.q) { entries.push(['q', state.q, 'Search: ' + state.q]); }
      chipsEl.hidden = !entries.length;
      if (!entries.length) { return; }
      entries.forEach(function (e) {
        var chip = document.createElement('span');
        chip.className = 'chip';
        var text = document.createElement('span');
        text.textContent = e[2];
        chip.appendChild(text);
        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'chip__remove';
        rm.setAttribute('aria-label', 'Remove filter: ' + e[2]);
        rm.setAttribute('data-chip-kind', e[0]);
        rm.setAttribute('data-chip-value', e[1]);
        rm.textContent = '×';
        chip.appendChild(rm);
        chipsEl.appendChild(chip);
      });
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'link-button chip-clear';
      clear.setAttribute('data-clear-all', '');
      clear.textContent = 'Clear all';
      chipsEl.appendChild(clear);
    }

    function syncControls() {
      boxes.forEach(function (b) { b.checked = inList(state[b.getAttribute('data-filter')], b.value); });
      if (searchEl && searchEl.value !== state.q && document.activeElement !== searchEl) { searchEl.value = state.q; }
      if (sortEl && sortEl.value !== state.sort) { sortEl.value = state.sort; }
      var n = activeCount();
      if (filterN) { filterN.textContent = n ? '(' + n + ')' : ''; }
    }

    function setView(v, remember) {
      state.view = v === 'list' ? 'list' : 'grid';
      grid.classList.toggle('is-list', state.view === 'list');
      viewBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-view-btn') === state.view ? 'true' : 'false'); });
      if (remember) { try { window.localStorage.setItem(VIEW_KEY, state.view); } catch (err) { /* ignore */ } }
    }

    function apply() {
      var famShown = 0, modelsShown = 0;
      fams.forEach(function (f) {
        var matching = f.rows.filter(function (r) { var ok = matches(r); r.hidden = !ok; return ok; });
        f.visible = matching.length > 0;
        f.cell.hidden = !f.visible;
        if (f.visible) { famShown += 1; modelsShown += matching.length; showModel(f, matching[0], matching); }
      });
      sortCells();
      var famTotal = fams.length, modelTotal = rows.length;
      var noun = function (n) { return n === 1 ? ' product' : ' products'; };
      var models = function (n) { return n === 1 ? ' model' : ' models'; };
      var text = famShown === famTotal ? famTotal + noun(famTotal) : famShown + ' of ' + famTotal + noun(famTotal);
      var sub = modelsShown > famShown || modelTotal > famTotal ? ', ' + modelsShown + models(modelsShown) : '';
      countEl.innerHTML = '';
      countEl.appendChild(document.createTextNode(text));
      if (sub) { var s = document.createElement('span'); s.className = 'result-count__models'; s.textContent = sub; countEl.appendChild(s); }
      if (applyBtn) { applyBtn.textContent = famShown ? 'Show ' + famShown + noun(famShown) : 'No matches'; }
      emptyEl.hidden = famShown !== 0;
      grid.hidden = famShown === 0;
      renderChips();
      syncControls();
    }

    function changed() { apply(); writeURL(); }

    function clearFilters() {
      KINDS.forEach(function (k) { state[k] = []; });
      state.q = '';
      if (searchEl) { searchEl.value = ''; }
      changed();
    }

    function renderTray() {
      while (trayItems.firstChild) { trayItems.removeChild(trayItems.firstChild); }
      state.compare.forEach(function (h) {
        var li = document.createElement('li');
        li.className = 'compare-tray__item';
        var name = document.createElement('span');
        name.className = 'compare-tray__name';
        name.textContent = nameOf[h];
        li.appendChild(name);
        var drop = document.createElement('button');
        drop.type = 'button';
        drop.className = 'chip__remove';
        drop.setAttribute('aria-label', 'Remove ' + nameOf[h] + ' from the comparison');
        drop.setAttribute('data-drop', h);
        drop.textContent = '×';
        li.appendChild(drop);
        trayItems.appendChild(li);
      });
      tray.hidden = state.compare.length === 0;
      trayHint.textContent = state.compare.length === 1
        ? 'Select one more model of the same category to compare.'
        : state.compare.length + ' models selected. Compare holds up to 4.';
      trayCompare.disabled = state.compare.length < 2;
      checkboxes.forEach(function (c) { c.checked = inList(state.compare, c.getAttribute('data-handle')); });
    }

    function buildTable() {
      while (regionTable.children.length > 1) { regionTable.removeChild(regionTable.lastChild); }
      var rows = ROWS[categoryOf[state.compare[0]]] || ROWS.tables;
      var thead = document.createElement('thead');
      var headRow = document.createElement('tr');
      var corner = document.createElement('th');
      corner.scope = 'col';
      corner.innerHTML = '<span class="sr-only">Specification</span>';
      headRow.appendChild(corner);
      state.compare.forEach(function (h) {
        var rec = facts[h] || {};
        var th = document.createElement('th');
        th.scope = 'col';
        var type = document.createElement('span');
        type.className = 'compare-table__type';
        type.textContent = rec.type || '';
        th.appendChild(type);
        th.appendChild(document.createTextNode(rec.name || nameOf[h]));
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      regionTable.appendChild(thead);
      var tbody = document.createElement('tbody');
      rows.forEach(function (row) {
        var values = state.compare.map(function (h) {
          var rec = facts[h] || {};
          return rec[row[1]] === undefined || rec[row[1]] === null ? 'Not published' : String(rec[row[1]]);
        });
        var differs = values.some(function (v) { return v !== values[0]; });
        var tr = document.createElement('tr');
        var rh = document.createElement('th');
        rh.scope = 'row';
        rh.appendChild(document.createTextNode(row[0]));
        if (differs) { var note = document.createElement('span'); note.className = 'sr-only'; note.textContent = ' (these models differ)'; rh.appendChild(note); }
        tr.appendChild(rh);
        values.forEach(function (v) { var td = document.createElement('td'); td.textContent = v; if (differs) { td.className = 'is-different'; } tr.appendChild(td); });
        tbody.appendChild(tr);
      });
      var linkRow = document.createElement('tr');
      var lh = document.createElement('th');
      lh.scope = 'row';
      lh.textContent = 'Product page';
      linkRow.appendChild(lh);
      state.compare.forEach(function (h) {
        var td = document.createElement('td');
        var a = document.createElement('a');
        a.className = 'text-link';
        a.href = (facts[h] && facts[h].url) ? facts[h].url : '/products/' + encodeURIComponent(h.split('@')[0]);
        a.appendChild(document.createTextNode('View Product'));
        var sr = document.createElement('span');
        sr.className = 'sr-only';
        sr.textContent = ', ' + (nameOf[h] || '');
        a.appendChild(sr);
        td.appendChild(a);
        linkRow.appendChild(td);
      });
      tbody.appendChild(linkRow);
      regionTable.appendChild(tbody);
    }

    function openCompare(moveFocus) {
      if (state.compare.length < 2) { return; }
      buildTable();
      region.hidden = false;
      catalog.hidden = true;
      if (closeBand) { closeBand.hidden = true; }
      state.panel = 'compare';
      writeURL();
      if (moveFocus) { regionTitle.focus(); }
    }

    function closeCompare(moveFocus) {
      region.hidden = true;
      catalog.hidden = false;
      if (closeBand) { closeBand.hidden = false; }
      state.panel = '';
      writeURL();
      if (moveFocus) { restoreFocus(state.compare[0]); }
    }

    function canTakeFocus(el) { return !!el && !el.disabled && !el.hidden && el.offsetParent !== null; }
    function restoreFocus(h) {
      if (canTakeFocus(trayCompare)) { trayCompare.focus(); return; }
      var box = checkboxes.filter(function (c) { return c.getAttribute('data-handle') === h; })[0];
      if (canTakeFocus(box)) { box.focus(); return; }
      for (var i = 0; i < checkboxes.length; i += 1) { if (canTakeFocus(checkboxes[i])) { checkboxes[i].focus(); return; } }
    }
    function focusAfterFilterChange() {
      if (canTakeFocus(searchEl)) { searchEl.focus(); return; }
      if (canTakeFocus(openBtn)) { openBtn.focus(); }
    }

    boxes.forEach(function (b) {
      b.addEventListener('change', function () {
        var kind = b.getAttribute('data-filter');
        if (b.checked) { if (!inList(state[kind], b.value)) { state[kind].push(b.value); } } else { dropFrom(state[kind], b.value); }
        changed();
      });
    });

    if (searchEl) {
      searchEl.addEventListener('input', function () {
        var value = this.value;
        if (searchTimer) { window.clearTimeout(searchTimer); }
        searchTimer = window.setTimeout(function () { state.q = value.trim(); changed(); }, 160);
      });
      searchEl.addEventListener('search', function () { state.q = this.value.trim(); changed(); });
      searchEl.addEventListener('keydown', function (event) { if (event.key === 'Enter') { event.preventDefault(); } });
    }
    if (sortEl) { sortEl.addEventListener('change', function () { state.sort = this.value; changed(); }); }

    chipsEl.addEventListener('click', function (event) {
      var target = event.target.closest('[data-chip-kind], [data-clear-all]');
      if (!target) { return; }
      if (target.hasAttribute('data-clear-all')) { clearFilters(); focusAfterFilterChange(); return; }
      var kind = target.getAttribute('data-chip-kind');
      if (kind === 'q') { state.q = ''; } else { dropFrom(state[kind], target.getAttribute('data-chip-value')); }
      changed();
      focusAfterFilterChange();
    });

    [].slice.call(catalog.querySelectorAll('.filters [data-clear-all], [data-empty] [data-clear-all]')).forEach(function (btn) {
      btn.addEventListener('click', function () { clearFilters(); });
    });

    checkboxes.forEach(function (box) {
      box.addEventListener('change', function () {
        var h = this.getAttribute('data-handle');
        if (!this.checked) { dropFrom(state.compare, h); renderTray(); writeURL(); return; }
        if (state.compare.length && categoryOf[state.compare[0]] !== categoryOf[h]) {
          this.checked = false;
          tray.hidden = false;
          trayHint.textContent = 'Compare works inside one category. Clear the selection to compare ' + (CATEGORY_NOUN[categoryOf[h]] || 'these') + '.';
          return;
        }
        if (state.compare.length >= 4) { this.checked = false; trayHint.textContent = 'Compare holds up to 4 models. Remove one to add another.'; return; }
        state.compare.push(h);
        renderTray();
        writeURL();
      });
    });

    trayItems.addEventListener('click', function (event) {
      var drop = event.target.closest('[data-drop]');
      if (!drop) { return; }
      var dropped = drop.getAttribute('data-drop');
      dropFrom(state.compare, dropped);
      renderTray();
      writeURL();
      restoreFocus(dropped);
    });
    trayClear.addEventListener('click', function () { var first = state.compare[0]; state.compare = []; renderTray(); writeURL(); restoreFocus(first); });
    trayCompare.addEventListener('click', function () { openCompare(true); });
    regionBack.addEventListener('click', function () { closeCompare(true); });

    /* drawer below 1024: the sidebar itself moves to <body> while open */
    var home = null;
    var DFOCUS = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
    function drawerMode() { return openBtn && window.getComputedStyle(openBtn).display !== 'none'; }
    function setInert(on) {
      [].slice.call(document.body.children).forEach(function (node) {
        if (node === drawer || node.tagName === 'SCRIPT') { return; }
        if (on) { node.setAttribute('inert', ''); } else { node.removeAttribute('inert'); }
      });
    }
    function openDrawer() {
      if (!drawer || drawer.classList.contains('is-open')) { return; }
      home = { parent: drawer.parentNode, next: drawer.nextSibling };
      document.body.appendChild(drawer);
      drawer.classList.add('is-open');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      setInert(true);
      document.documentElement.style.overflow = 'hidden';
      openBtn.setAttribute('aria-expanded', 'true');
      var first = drawer.querySelector('[data-drawer-close]');
      if (first) { first.focus(); }
    }
    function closeDrawer(returnFocus) {
      if (!drawer || !drawer.classList.contains('is-open')) { return; }
      drawer.classList.remove('is-open');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      setInert(false);
      document.documentElement.style.overflow = '';
      if (home) { home.parent.insertBefore(drawer, home.next); }
      openBtn.setAttribute('aria-expanded', 'false');
      if (returnFocus && canTakeFocus(openBtn)) { openBtn.focus(); }
    }
    if (openBtn && drawer) {
      openBtn.addEventListener('click', openDrawer);
      [].slice.call(drawer.querySelectorAll('[data-drawer-close]')).forEach(function (btn) { btn.addEventListener('click', function () { closeDrawer(true); }); });
      document.addEventListener('keydown', function (event) {
        if (!drawer.classList.contains('is-open')) { return; }
        if (event.key === 'Escape') { event.preventDefault(); closeDrawer(true); return; }
        if (event.key !== 'Tab') { return; }
        var list = [].slice.call(drawer.querySelectorAll(DFOCUS)).filter(function (el) { return el.offsetParent !== null; });
        if (!list.length) { return; }
        if (event.shiftKey && document.activeElement === list[0]) { event.preventDefault(); list[list.length - 1].focus(); }
        else if (!event.shiftKey && document.activeElement === list[list.length - 1]) { event.preventDefault(); list[0].focus(); }
      });
      window.addEventListener('resize', function () { if (drawer.classList.contains('is-open') && !drawerMode()) { closeDrawer(false); } });
    }

    viewBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        setView(b.getAttribute('data-view-btn'), true);
        writeURL();
      });
    });

    window.addEventListener('popstate', function () {
      readURL(); setView(state.view, false); apply(); renderTray();
      if (state.panel === 'compare' && state.compare.length >= 2) { openCompare(false); } else { closeCompare(false); }
    });

    readURL();
    setView(state.view, false);
    apply();
    renderTray();
    if (state.panel === 'compare' && state.compare.length >= 2) { openCompare(false); }
    else if (state.panel) { state.panel = ''; writeURL(); }
  }


  /* ======================================================================
     PRODUCT: gallery (color keeps view), options, model links, quantity
     pricing, Add to Cart, Buy Now, Get a Project Quote, tabs, lightbox.
     URL keeps ?<option>=&qty=&angle= (angle is the gallery view; Shopify
     reserves ?view= for alternate templates) plus ?variant=.
     ====================================================================== */

  var SLOTS = ['primary', 'dimensions', 'detail', 'scene', 'extra'];
  var SLOT_LABEL = { primary: 'Product', dimensions: 'Dimensions', detail: 'Detail', scene: 'In use', extra: 'Another view' };
  var SLOT_FIT = { primary: 'contain', dimensions: 'contain', extra: 'contain', detail: 'cover', scene: 'cover' };

  /* a gallery record {f, ws, w, h, alt, il} as image sources */
  function viewRec(rec, slot) {
    var ws = (rec.ws || []).slice().sort(function (a, b) { return a - b; });
    var srcset = ws.map(function (w) { return assetUrl(rec.f + '-' + w + '.webp') + ' ' + w + 'w'; }).join(', ');
    return {
      slot: slot, label: rec.label || SLOT_LABEL[slot] || slot, zoom: rec.zoom || 1, note: rec.note || '', alt: rec.alt || '', illustration: !!rec.il || slot === 'scene',
      fit: SLOT_FIT[slot] || 'contain', w: rec.w, h: rec.h,
      src: assetUrl(rec.f + '-' + ws[ws.length - 1] + '.webp'), srcset: srcset,
      thumb: assetUrl(rec.f + '-' + ws[0] + '.webp')
    };
  }

  function initProduct() {
    var root = document.querySelector('[data-pdp]');
    if (!root) { return; }
    var data = readJSON('bz-pdp-data');
    if (!data || !data.variants || !data.variants.length) { return; }
    var gal = readJSON('bz-pdp-gallery') || { base: null, order: [], colors: {} };
    var colorIndex = Number(data.colorOption);
    var selected = (data.selected || []).slice();

    var els = {
      unit: root.querySelector('[data-unit-price]'),
      unitNote: root.querySelector('[data-unit-note]'),
      sum: root.querySelector('[data-total-sum]'),
      totalNote: root.querySelector('[data-total-note]'),
      ladder: root.querySelector('[data-ladder]'),
      qty: root.querySelector('[data-qty]'),
      qtyError: root.querySelector('[data-qty-error]'),
      add: root.querySelector('[data-add]'),
      quote: root.querySelector('[data-quote]'),
      buyNow: root.querySelector('[data-buy-now]'),
      buyNote: root.querySelector('[data-buy-note]'),
      missing: root.querySelector('[data-missing]'),
      missingTitle: root.querySelector('[data-missing-title]'),
      famLinks: [].slice.call(root.querySelectorAll('[data-fam-link]')),
      confirm: root.querySelector('[data-confirm]'),
      sku: root.querySelector('[data-fact-sku]'),
      weight: root.querySelector('[data-fact-weight]'),
      specWeight: document.querySelector('[data-spec-weight]'),
      rail: root.querySelector('[data-rail]'),
      stage: root.querySelector('.gal__stage'),
      main: root.querySelector('[data-main]'),
      caption: root.querySelector('[data-gallery-caption]'),
      deliv: root.querySelector('[data-deliv-line]'),
      qtyCap: root.querySelector('[data-qty-co]')
    };

    /* lids and spare liners chosen on a receptacle page ride on the same order,
       one per receptacle (they are not sold on their own) */
    var addonBox = root.querySelector('[data-addons]');
    function addonLines(q) {
      if (!addonBox) { return []; }
      var out = [];
      [].slice.call(addonBox.querySelectorAll('[data-addon]')).forEach(function (el) {
        var src = el;
        if (el.tagName === 'SELECT') { src = el.options[el.selectedIndex]; if (!el.value) { return; } }
        else if (!el.checked) { return; }
        var price = Number(src.getAttribute('data-price')) || 0;
        out.push({ id: String(el.value), qty: q, grams: Number(src.getAttribute('data-grams')) || 0, base: price, unit: price, x: 0,
          title: src.getAttribute('data-title') || 'add-on', parcel: false });
      });
      return out;
    }
    function addonCents(q) { return addonLines(q).reduce(function (s, l) { return s + l.base * l.qty; }, 0); }
    function addonGrams(q) { return addonLines(q).reduce(function (s, l) { return s + l.grams * l.qty; }, 0); }
    if (addonBox) { addonBox.addEventListener('change', function () { renderPrice(); }); }

    var view = { slot: 'primary' };
    var shown = { slot: 'primary', color: gal.base };
    var lastQty = 1;
    var co = data.checkout || { on: false, ok: false, reason: '', maxLb: 700 };
    var urlNote = null;

    function sameOpts(a, b) { for (var i = 0; i < a.length; i += 1) { if (a[i] !== b[i]) { return false; } } return true; }
    function currentVariant() {
      for (var i = 0; i < data.variants.length; i += 1) { if (sameOpts(data.variants[i].opts, selected)) { return data.variants[i]; } }
      return null;
    }
    function existsWith(index, value) {
      return data.variants.some(function (v) {
        if (v.opts[index] !== value) { return false; }
        for (var j = 0; j < v.opts.length; j += 1) { if (j !== index && v.opts[j] !== selected[j]) { return false; } }
        return true;
      });
    }
    function resolve(index, value) {
      var best = null, bestScore = -1;
      data.variants.forEach(function (v) {
        if (v.opts[index] !== value) { return; }
        var score = 0;
        for (var j = 0; j < v.opts.length; j += 1) { if (v.opts[j] === selected[j]) { score += 1; } }
        if (score > bestScore) { best = v; bestScore = score; }
      });
      if (!best) { return []; }
      var moved = [];
      for (var k = 0; k < best.opts.length; k += 1) {
        if (k !== index && best.opts[k] !== selected[k]) { moved.push(String(data.optionNames[k]).toLowerCase() + ' set to ' + best.opts[k]); }
      }
      selected = best.opts.slice();
      return moved;
    }

    /* the quantity being priced is always the one shown in the field */
    function qty() { return lastQty; }
    function onQtyInput() {
      var q = parseQty(els.qty.value);
      if (q === null) {
        var raw = els.qty.value.trim();
        els.qtyError.textContent = raw !== '' && /^[0-9]+$/.test(raw) && Number(raw) > 999
          ? 'Up to 999 on this page. Larger quantities are priced in your quote.'
          : 'Enter a whole number from 1 to 999.';
        els.qty.setAttribute('aria-invalid', 'true');
        return false;
      }
      els.qtyError.textContent = '';
      els.qty.setAttribute('aria-invalid', 'false');
      lastQty = q;
      return true;
    }
    function commitQty() {
      var ok = onQtyInput();
      if (!ok) {
        els.qtyError.textContent = 'Enter a whole number from 1 to 999. The quantity stays at ' + lastQty + '.';
      }
      els.qty.value = String(lastQty);
      els.qty.setAttribute('aria-invalid', ok ? 'false' : 'true');
      renderPrice();
      writeURL();
    }

    function ladderMarkup(v, q) {
      if (!v.ladder || !v.ladder.length) { return '<p class="t-utility" data-ladder-table>Quantity pricing confirmed in your quote.</p>'; }
      var rows = [{ min: 1, max: v.ladder[0][0] - 1, price: v.price }];
      for (var i = 0; i < v.ladder.length; i += 1) {
        rows.push({ min: v.ladder[i][0], max: i + 1 < v.ladder.length ? v.ladder[i + 1][0] - 1 : null, price: v.ladder[i][1] });
      }
      var html = '<table class="data-table ladder-table" data-ladder-table><caption class="sr-only">Quantity pricing for this configuration</caption>'
        + '<thead><tr><th scope="col">Quantity</th><th scope="col">Unit price</th></tr></thead><tbody>';
      rows.forEach(function (r) {
        var active = q >= r.min && (r.max === null || q <= r.max);
        var range = r.max === null ? r.min + ' or more' : r.min + ' to ' + r.max;
        html += '<tr' + (active ? ' class="is-active" aria-current="true"' : '') + '><th scope="row">' + range + '</th><td class="num">' + money(r.price) + '</td></tr>';
      });
      return html + '</tbody></table>';
    }

    function renderPrice() {
      var v = currentVariant();
      if (!v) { return; }
      var q = qty();
      var unit = unitPrice(v.ladder, v.price, q);
      els.unit.textContent = money(unit);
      els.unitNote.textContent = unit < v.price ? 'each at quantity ' + q + ', list ' + money(v.price) : 'each';
      var extraCents = addonCents(q);
      els.sum.textContent = money(unit * q + extraCents);
      var totalLb = gramsToLbExact(v.grams * q + addonGrams(q));
      els.totalNote.textContent = q + ' at ' + money(unit) + (extraCents ? ', add-ons ' + money(extraCents) : '') + (totalLb !== null ? ', ' + lbShow(totalLb, bandLimits(shipFreight || null, co.maxLb || 700)) + ' crated' : ', crated weight confirmed in your quote');
      els.ladder.innerHTML = ladderMarkup(v, q);
      if (els.sku) { els.sku.textContent = v.sku || ''; }
      var eachLb = gramsToLb(v.grams);
      var lbLine = eachLb !== null ? lbText(eachLb) : 'Confirmed in your quote';
      if (els.weight) { els.weight.textContent = lbLine; }
      if (els.specWeight) { els.specWeight.textContent = lbLine; }
      if (els.qtyCap) {
        /* most units of this configuration one online checkout carries (exact weight, like checkoutVerdict) */
        var unitGrams = (Number(v.grams) || 0) + addonGrams(1);
        var maxLbCo = co.maxLb || 700;
        var cap = unitGrams > 0 ? Math.floor(maxLbCo * GRAMS_PER_LB / unitGrams + 1e-9) : 0;
        var rung = v.ladder && v.ladder.length ? Number(v.ladder[0][0]) : 0;
        /* poolOnline false: no mix of rung units on this ladder fits one online order (picnic tables and
           slatted receptacles: the lightest weighs 163 lb, so 5 are over 700 lb) */
        if (rung && cap < rung && data.poolOnline === false) {
          els.qtyCap.textContent = 'Quantity prices from ' + rung + ' units are applied by project quote, at the prices shown: online checkout carries up to ' + cap + ' of these (' + lbText(maxLbCo) + ' per order).';
        } else {
          els.qtyCap.textContent = 'Quantity prices apply at checkout for orders up to ' + lbText(maxLbCo) + (cap >= 1 ? ' (up to ' + cap + ' of these online)' : '') + '; larger orders get the same prices by project quote.';
        }
      }
      renderBuy(v, q, unit);
    }

    /* Buy Now: Shopify checkout at list price for this configuration. */
    /* Freight to your ZIP: the amount checkout charges for this configuration and quantity */
    var shipZip = root.querySelector('[data-ship-zip]');
    var shipOut = root.querySelector('[data-ship-out]');
    var shipService = root.querySelector('[data-ship-service]');
    var shipFreight = readJSON('bz-freight');
    var shipQuote = null;
    if (shipZip && !shipZip.value && savedZip()) { shipZip.value = savedZip(); }
    function renderShip(v, q) {
      shipQuote = null;
      if (!shipZip || !shipOut || !shipFreight) { return; }
      var zip = shipZip.value.replace(/[^0-9]/g, '').slice(0, 5);
      if (zip.length !== 5) { shipOut.textContent = ''; return; }
      saveZip(zip);
      if (!v) { shipOut.textContent = ''; return; }
      var lbx = gramsToLbExact((Number(v.grams) || 0) * q + addonGrams(q));
      var lb = lbShow(lbx, bandLimits(shipFreight, co.maxLb || 700));
      var fq = freightQuote(shipFreight, zip, data.parcel ? 0 : lbx, !!data.parcel);
      shipQuote = fq;
      var place = fq.state || 'ZIP ' + zip;
      if (!co.ok) {
        shipOut.textContent = 'Freight for this model to ' + place + ' is priced in your project quote.' + (fq.zone ? ' ' + transitText(fq.zone) + '.' : '');
        return;
      }
      if (!data.parcel && !(Number(v.grams) > 0)) { shipOut.textContent = 'Freight to ' + place + ' is confirmed in your quote.'; return; }
      if (fq.status === 'unmapped') { shipOut.textContent = 'Online checkout ships crated freight to the lower 48 states and DC. For ' + place + ', request a project quote.'; return; }
      var orderCents = unitPrice(v.ladder, v.price, q) * q + addonCents(q);
      if (freeFreightFor(shipFreight, orderCents, fq)) {
        shipOut.innerHTML = '';
        var fstrong = document.createElement('strong');
        fstrong.textContent = 'Free freight';
        shipOut.appendChild(fstrong);
        shipOut.appendChild(document.createTextNode(' to ' + place + '. ' + freeFreightText(shipFreight) + (fq.status === 'over' ? ' At ' + lb + ' the order is placed through a project quote.' : '') + ' ' + transitText(fq.zone) + '.'));
        return;
      }
      var elseNote = overFreeMinElsewhere(shipFreight, orderCents, fq) ? freeElsewhereText(shipFreight, place) : '';
      if (fq.status === 'over') { shipOut.textContent = 'At ' + lb + ', freight to ' + place + ' is priced in a project quote: the online table covers up to ' + lbText(co.maxLb || 700) + '. ' + transitText(fq.zone) + '.' + elseNote; return; }
      if (data.parcel) {
        shipOut.textContent = money(fq.totalUsd * 100) + ' by parcel to ' + place + ', added once per order.';
        return;
      }
      var delivery = deliveryRange(shipFreight, shipService ? shipService.value : 'unknown');
      shipOut.textContent = freightText('freight_to', { amount: moneyRange(fq.totalUsd + delivery.min, fq.totalUsd + delivery.max), place: place, quantity: q, weight: lb })
        + ' ' + transitText(fq.zone) + '. ' + (delivery.known ? freightText('choose_rate', { rate: delivery.rate }) : freightText('unknown_service') + ' ' + freightText('choose_matching')) + elseNote;
    }
    if (shipZip) {
      shipZip.addEventListener('input', function () {
        var cleaned = shipZip.value.replace(/[^0-9]/g, '').slice(0, 5);
        if (cleaned !== shipZip.value) { shipZip.value = cleaned; }
        renderPrice();
      });
    }

    if (shipService) { shipService.addEventListener('change', function () { renderPrice(); }); }

    /* v4.9.4 delivered price above the buttons: goods (quantity price x quantity + add-ons)
       plus the freight checkout charges, from freightQuote and checkoutVerdict above. */
    function zoneSpread(fr, lb) {
      var zones = (fr && fr.zones) || [];
      var tz = freightZone(fr, 'Texas');
      var tx = null, lo = null, hi = null;
      zones.forEach(function (z) {
        var b = freightBand(z, lb);
        if (!b) { return; }
        var u = Number(b.usd) || 0;
        if (lo === null || u < lo) { lo = u; }
        if (hi === null || u > hi) { hi = u; }
        if (tz && Number(z.zone) === Number(tz.zone)) { tx = u; }
      });
      return hi === null ? null : { tx: tx === null ? lo : tx, min: lo, max: hi };
    }
    function extrasText(fr) {
      var lift = Number(fr && fr.liftgate_usd) || 0, lim = Number(fr && fr.limited_usd) || 0;
      if (!lift && !lim) { return ''; }
      /* limited access depends on the type of site, not on the dock (freight page, servicesText) */
      return [lift ? '; +' + money(lift * 100) + ' liftgate if there is no dock or forklift' : '',
        lim ? (lift ? ', or ' : '; ') + '+' + money(lim * 100) + ' limited access (liftgate included) for schools, parks, campgrounds, churches, homes and job sites' : ''].join('');
    }
    function renderDelivered(v, q, unit, verdict) {
      var el = els.deliv;
      if (!el) { return; }
      function put(lead, rest) {
        el.innerHTML = '';
        var st = document.createElement('strong');
        st.textContent = lead;
        el.appendChild(st);
        if (rest) { el.appendChild(document.createTextNode(rest)); }
      }
      if (!co.on || !co.ok) { put('Delivered price by project quote.', co.reason ? ' ' + co.reason : ''); return; }
      if (!shipFreight) { return; }
      var goods = unit * q + addonCents(q);
      var lb = gramsToLbExact((Number(v.grams) || 0) * q + addonGrams(q));
      if (!data.parcel && lb === null) { put('Delivered price by project quote.', ' Freight for this model is confirmed in your quote.'); return; }
      var freeMin = Number(shipFreight.free_over_usd) || 0;
      var fq = shipQuote;
      var delivery = deliveryRange(shipFreight, data.parcel ? 'dock' : (shipService ? shipService.value : 'unknown'));
      if (fq && fq.status !== 'nozip') {
        var place = fq.state || 'ZIP ' + fq.zip;
        if (!verdict.ok || fq.status !== 'ok') { put('Delivered price to ' + place + ' by project quote.', verdict.reason ? ' ' + verdict.reason : ''); return; }
        if (freeFreightFor(shipFreight, goods, fq)) {
          put('Delivered to ' + place + ': ' + money(goods), ', free freight on orders of ' + money(freeMin * 100) + ' or more to ' + freeStatesText(shipFreight) + ', curbside, liftgate or limited access included.');
          return;
        }
        put(freightText('delivered_to', { place: place, amount: moneyRange(goods / 100 + fq.totalUsd + delivery.min, goods / 100 + fq.totalUsd + delivery.max) }),
          ' ' + freightText(delivery.known ? 'selected_service' : 'unknown_service'));
        return;
      }
      if (!verdict.ok) { put('Delivered price by project quote.', verdict.reason ? ' ' + verdict.reason : ''); return; }
      var parcelUsd = shipFreight.parcel ? Number(shipFreight.parcel.usd) || 0 : 0;
      var spread = data.parcel ? { tx: parcelUsd, min: parcelUsd, max: parcelUsd } : zoneSpread(shipFreight, lb);
      if (!spread) { put('Delivered price by project quote.', ' Freight for this shipment is priced in your quote.'); return; }
      if (freeMin && goods >= freeMin * 100) { put('Delivered for ' + money(goods) + ' to ' + freeStatesText(shipFreight), ': free freight on orders of ' + money(freeMin * 100) + ' or more, curbside. Other states pay freight; enter your ZIP for your exact price.'); return; }
      put(freightText('delivered_total', { amount: moneyRange(goods / 100 + spread.min + delivery.min, goods / 100 + spread.max + delivery.max) }),
        ' ' + freightText('all_zones') + ' ' + freightText('initial_range'));
    }

    function renderBuy(v, q, unit) {
      renderShip(v, q);
      var verdict = checkoutVerdict([{ id: String(v.id), qty: q, grams: Number(v.grams) || 0, base: v.price, unit: unit, x: 0, title: data.shortName, parcel: !!data.parcel }].concat(addonLines(q)), co.maxLb, shipQuote);
      renderDelivered(v, q, unit, verdict);
      if (!els.buyNote || !co.on) { return; }
      if (!co.ok) { els.buyNote.textContent = co.reason; els.buyNote.className = 'buy-note'; return; }
      if (verdict.ok && shipQuote && shipQuote.zip && shipQuote.zip.length === 5) {
        verdict.url += '?checkout%5Bshipping_address%5D%5Bzip%5D=' + shipQuote.zip + '&checkout%5Bshipping_address%5D%5Bcountry%5D=US&attributes%5BDelivery%20ZIP%5D=' + shipQuote.zip + '&attributes%5BOrdered%20from%5D=Brazos%20product%20page';
      }
      els.buyNote.className = 'buy-note' + (verdict.ok ? '' : ' buy-note--warn');
      els.buyNote.innerHTML = '';
      if (!verdict.ok) {
        els.buyNote.appendChild(document.createTextNode(verdict.reason + ' '));
        var a = document.createElement('a');
        a.href = els.quote ? els.quote.getAttribute('href') : data.projectUrl;
        a.textContent = 'Get a Project Quote';
        a.setAttribute('data-quote-alt', '');
        els.buyNote.appendChild(a);
      } else if (unit < v.price && shipFreight && shipFreight.qty_at_checkout) {
        var strong = document.createElement('strong');
        strong.textContent = money(unit * q);
        els.buyNote.appendChild(document.createTextNode('Buy Now checks out ' + q + ' at the quantity price, ' + money(unit) + ' each: '));
        els.buyNote.appendChild(strong);
        els.buyNote.appendChild(document.createTextNode(' before freight and tax. The discount is applied automatically at checkout.'));
      } else if (unit < v.price) {
        var strongL = document.createElement('strong');
        strongL.textContent = money(v.price * q);
        els.buyNote.appendChild(document.createTextNode('Buy Now checks out at the list price, ' + money(v.price) + ' each: '));
        els.buyNote.appendChild(strongL);
        els.buyNote.appendChild(document.createTextNode(' before freight and tax. The quantity price, ' + money(unit) + ' each, is honored on a project quote.'));
      } else {
        els.buyNote.textContent = 'Buy Now checks out this configuration. ' + (shipQuote && shipQuote.status === 'ok' ? 'Freight as shown above; tax is added at checkout.' : 'Freight and tax are calculated at checkout.');
      }
      if (els.buyNow) {
        els.buyNow.setAttribute('href', verdict.ok ? verdict.url : '#');
        els.buyNow.classList.toggle('is-disabled', !verdict.ok);
        if (verdict.ok) { els.buyNow.removeAttribute('aria-disabled'); } else { els.buyNow.setAttribute('aria-disabled', 'true'); }
      }
    }

    /* Gallery identity includes mounting / lid type, not just finish color. */
    function activeGallery() {
      var index = (data.optionNames || []).indexOf(gal.variantOption);
      return index >= 0 && gal.variants && gal.variants[selected[index]] ? gal.variants[selected[index]] : gal;
    }
    function renderDimensionFacts() {
      var d = activeGallery().dimensionFacts;
      if (!d) { return; }
      var overview = document.getElementById('panel-overview');
      if (overview) {
        [].slice.call(overview.querySelectorAll('tr')).forEach(function(row) {
          var label = row.querySelector('td, th');
          if (label && /^(overall|seat height|table height|dimensions|footprint|assembled|length|width|height)/i.test(label.textContent.trim())) { row.hidden = true; }
        });
        if (!overview.querySelector('[data-dimension-reference]')) {
          var hint = document.createElement('p'); hint.setAttribute('data-dimension-reference',''); hint.className = 't-utility u-muted'; hint.textContent = 'Dimensions for the selected configuration are shown under Specifications and in the gallery reference cards.'; overview.insertBefore(hint,overview.querySelector('.tabs__heading').nextSibling);
        }
        if (/^46in-(round|square)-expanded-metal-picnic-table$/.test(data.handle) && !overview.dataset.mountCopyCorrected) {
          var walker = document.createTreeWalker(overview,NodeFilter.SHOW_TEXT); var node;
          while ((node = walker.nextNode())) {
            node.nodeValue = node.nodeValue
              .replace(/legs extend below grade for setting in concrete/g,'a central post extends below grade for permanent installation')
              .replace(/The in-ground version uses longer leg tubes[^.]*\./g,'The in-ground model uses a central post; confirm foundation and embedment requirements before installation.')
              .replace(/In-ground has extended legs that you set into poured concrete footings; once set, it is permanent\./g,'The in-ground model uses a central post for permanent installation. Confirm foundation and embedment requirements before site preparation.')
              .replace(/legs embed in concrete/g,'central-post configuration')
              .replace(/central leg cluster/g,'support structure')
              .replace(/confirm the leg spacing from the assembly sheet available on request first/g,'confirm foundation and embedment requirements from the installation drawing first')
              .replace(/Most crews set the table in wet concrete rather than pre-forming holes\./g,'');
          }
          overview.dataset.mountCopyCorrected = 'true';
        }
      }
      var panel = document.getElementById('panel-specs');
      if (!panel) { return; }
      var old = panel.querySelector('.spec-figures');
      if (old) { old.hidden = true; old.style.display = 'none'; }
      var legacy = panel.querySelector('[data-legacy-detail-dimensions]'); if (legacy) { legacy.hidden = true; legacy.style.display = 'none'; }
      var missing = panel.querySelector(':scope > .t-lede'); if (missing) { missing.hidden = true; }
      var scope = panel.querySelector('.spec-figures__scope');
      if (scope) { scope.hidden = true; }
      var target = panel.querySelector('[data-verified-dimensions]');
      if (!target) { target = document.createElement('div'); target.setAttribute('data-verified-dimensions',''); panel.insertBefore(target,panel.querySelector('.tabs__heading').nextSibling); }
      target.innerHTML = '';
      var dl = document.createElement('dl'); dl.className = 'spec-figures';
      [['length','Overall length'],['width','Overall width'],['height','Height'],['seat_height','Seat height']].forEach(function(pair) {
        var n = (d.values || {})[pair[0]];
        if (n === null || typeof n === 'undefined') { return; }
        var div = document.createElement('div'), dt = document.createElement('dt'), dd = document.createElement('dd');
        dt.textContent = pair[1]; dd.textContent = n + ' in'; div.appendChild(dt); div.appendChild(dd); dl.appendChild(div);
      });
      if (dl.children.length) { target.appendChild(dl); }
      var note = document.createElement('p'); note.className = 't-utility u-muted u-mt-m'; note.textContent = d.note || ''; target.appendChild(note);
      /* no outbound link to the drawing's source: supplier-privacy rule; the drawing itself is in the gallery */
    }
    /* gallery */
    function selectedColor() { return colorIndex >= 0 ? selected[colorIndex] : activeGallery().base; }
    function shownColor() {
      var c = selectedColor();
      if (c && activeGallery().colors[c] && Object.keys(activeGallery().colors[c]).length) { return c; }
      return activeGallery().base;
    }
    function slotsFor(color) { var have = activeGallery().colors[color] || {}; return SLOTS.filter(function (s) { return !!have[s]; }); }
    function nearestSlot(color, wanted) {
      var list = slotsFor(color);
      if (!list.length) { return null; }
      if (list.indexOf(wanted) > -1) { return wanted; }
      var at = SLOTS.indexOf(wanted), best = list[0], dist = 99;
      list.forEach(function (s) { var d = Math.abs(SLOTS.indexOf(s) - at); if (d < dist) { dist = d; best = s; } });
      return best;
    }
    function rec(color, slot) { return viewRec(activeGallery().colors[color][slot], slot); }
    function lower(s) { return String(s || '').toLowerCase(); }
    function captionText(color, slot, wantedSlot) {
      var r = rec(color, slot);
      var parts = [];
      if (r.illustration) { parts.push('Illustration.'); }
      parts.push(r.label + ' view' + (colorIndex >= 0 ? ', ' + color : '') + '.');
      var sel = selectedColor();
      if (sel && sel !== color) { parts.push('Images in ' + sel + ' are in preparation; shown in ' + color + '.'); }
      else if (wantedSlot && wantedSlot !== slot) { parts.push('The ' + lower(SLOT_LABEL[wantedSlot] || wantedSlot) + ' view is not ready in ' + color + ' yet; showing the ' + lower(r.label) + ' view.'); }
      if (r.note) { parts.push(r.note); }
      if (gal.variantOption) { var oi = (data.optionNames || []).indexOf(gal.variantOption); if (oi >= 0) { parts.push('Selected: ' + selected[oi] + '.'); } }
      if (data.receptacle && !gal.variantOption) { parts.push('Accessories shown may differ.'); }
      return parts.join(' ');
    }
    function setImg(img, r, sizes) {
      img.setAttribute('srcset', r.srcset);
      img.setAttribute('sizes', sizes);
      img.setAttribute('src', r.src);
      img.setAttribute('width', r.w);
      img.setAttribute('height', r.h);
      img.setAttribute('alt', r.alt);
      img.style.transform = r.zoom > 1 ? 'scale(' + r.zoom + ')' : '';
      img.style.transformOrigin = '50% 48%';
      img.parentElement.style.overflow = 'hidden';
      img.parentElement.style.display = 'block';
    }
    function renderRail(color, slot) {
      if (!els.rail) { return; }
      els.rail.innerHTML = slotsFor(color).map(function (s) {
        var r = rec(color, s);
        return '<button class="gal__thumb" type="button" data-slot="' + s + '" aria-pressed="' + (s === slot ? 'true' : 'false') + '">'
          + '<span class="gal__thumb-img gal__thumb-img--' + r.fit + '"><img src="' + esc(r.thumb) + '" alt="" width="' + r.w + '" height="' + r.h + '" decoding="async"></span>'
          + '<span class="gal__thumb-label">' + esc(r.label) + ' view' + (colorIndex >= 0 ? ', ' + esc(color) : '') + '</span></button>';
      }).join('');
    }
    var lastShown = '';
    /* lids: a style without its own photos shows a note, never another style */
    function renderMissing(sel) {
      if (!els.missing) { return false; }
      var missing = !!data.strictGallery && !!sel && !(activeGallery().colors[sel] && Object.keys(activeGallery().colors[sel]).length);
      els.missing.hidden = !missing;
      if (els.stage) { els.stage.hidden = missing; }
      /* keep the rail's grid column; only hide its thumbnails */
      if (els.rail) { els.rail.style.visibility = missing ? 'hidden' : ''; }
      var enl = root.querySelector('.gal__enlarge');
      if (enl) { enl.hidden = missing; }
      if (missing) {
        if (els.missingTitle) { els.missingTitle.textContent = sel; }
        els.caption.textContent = 'Photos of the ' + sel.toLowerCase() + ' are in preparation. The style you select is the one in your order.';
      }
      return missing;
    }
    function renderGallery(wantedSlot) {
      if (!els.main) { return; }
      if (renderMissing(selectedColor())) { return; }
      var color = shownColor();
      if (!color || !activeGallery().colors[color]) { return; }
      var slot = nearestSlot(color, wantedSlot || view.slot);
      if (!slot) { return; }
      var r = rec(color, slot);
      var key = color + '|' + slot + '|' + r.src;
      var img = els.main.querySelector('img');
      if (key !== lastShown && img) {
        els.stage.classList.add('is-swapping');
        img.onload = function () { els.stage.classList.remove('is-swapping'); };
        window.setTimeout(function () { els.stage.classList.remove('is-swapping'); }, 400);
        setImg(img, r, '(max-width: 900px) 92vw, 50vw');
        els.stage.classList.toggle('gal__stage--contain', r.fit === 'contain');
        els.stage.classList.toggle('gal__stage--cover', r.fit !== 'contain');
        els.stage.setAttribute('aria-label', 'Enlarge the ' + lower(r.label) + ' view' + (colorIndex >= 0 ? ', ' + color : ''));
        lastShown = key;
      }
      renderRail(color, slot);
      els.caption.textContent = captionText(color, slot, view.slot);
      shown.slot = slot;
      shown.color = color;
    }

    function renderOptions(notes) {
      [].slice.call(root.querySelectorAll('fieldset[data-option]')).forEach(function (set) {
        var index = Number(set.getAttribute('data-option'));
        var valueEl = set.querySelector('[data-option-value]');
        if (valueEl) { valueEl.textContent = String(selected[index]).replace('In-Ground', 'In-ground'); }
        [].slice.call(set.querySelectorAll('input[data-option]')).forEach(function (radio) {
          radio.checked = radio.value === selected[index];
          var wrap = radio.closest('.swatch, .opt-choice');
          /* Mount is chosen first, so it is never shown as unavailable: the color adapts to it */
          var ok = set.classList.contains('fam-opt--mount') ? true : existsWith(index, radio.value);
          if (wrap) { wrap.classList.toggle('is-unavailable', !ok); }
          if (wrap && wrap.classList.contains('swatch')) {
            var label = radio.value + (ok ? '' : ' (changes another option)');
            var sr = wrap.querySelector('.sr-only');
            if (sr) { sr.textContent = label; }
            wrap.title = label;
          }
        });
        var note = set.querySelector('[data-option-note]');
        if (note) { note.textContent = notes && notes.index === index ? notes.text : ''; }
      });
    }

    function readURL() {
      var params = new URLSearchParams(window.location.search);
      (data.optionParams || []).forEach(function (p) {
        var v = params.get(p.key);
        if (v && p.values.indexOf(v) > -1) { resolve(p.i, v); }
        else if (v && !urlNote) { urlNote = { index: p.i, key: p.key, value: v }; }
      });
      var q = parseQty(params.get('qty'));
      if (q) { lastQty = q; els.qty.value = String(q); }
      var slot = params.get('angle');
      if (slot && SLOTS.indexOf(slot) > -1) { view.slot = slot; }
    }
    function writeURL() {
      var params = new URLSearchParams(window.location.search);
      (data.optionParams || []).forEach(function (p) { params.set(p.key, selected[p.i]); });
      var v = currentVariant();
      if (v) { params.set('variant', String(v.id)); }
      if (view.slot && view.slot !== 'primary') { params.set('angle', view.slot); } else { params.delete('angle'); }
      params.delete('view');
      params.delete('from');
      if (qty() > 1) { params.set('qty', String(qty())); } else { params.delete('qty'); }
      var query = params.toString();
      try { window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : '')); } catch (err) { /* ignore */ }
      updateFamLinks();
    }

    /* model links (same family, other product pages) carry color, quantity and view */
    function updateFamLinks() {
      if (!els.famLinks.length) { return; }
      var carry = new URLSearchParams();
      if (colorIndex >= 0 && selected[colorIndex] && !data.strictGallery) { carry.set('color', selected[colorIndex]); }
      if (qty() > 1) { carry.set('qty', String(qty())); }
      if (view.slot && view.slot !== 'primary') { carry.set('angle', view.slot); }
      carry.set('from', data.handle);
      var mountIndex = (data.optionNames || []).indexOf('Mount');
      var ground = mountIndex >= 0 && selected[mountIndex] === 'In-Ground';
      els.famLinks.forEach(function (a) {
        var target = a.getAttribute('data-fam-target');
        if (!target) { return; }
        var params = new URLSearchParams(carry.toString());
        var extra = a.getAttribute('data-fam-extra');
        if (extra) { new URLSearchParams(extra).forEach(function (v, k) { params.set(k, v); }); }
        else if (ground && a.getAttribute('data-fam-mounts') === 'both') { params.set('mount', 'In-Ground'); }
        var q = params.toString();
        a.setAttribute('href', '/products/' + encodeURIComponent(target) + (q ? '?' + q : ''));
        var only = a.querySelector('.fam-choice__only');
        if (only) {
          var portableOnly = ground && a.getAttribute('data-fam-mounts') === 'portable';
          only.hidden = !portableOnly;
          a.classList.toggle('is-portable-only', portableOnly);
        }
      });
    }

    function addToProject() {
      if (parseQty(els.qty.value) === null) { commitQty(); els.qty.focus(); return false; }
      var v = currentVariant();
      if (!v) { return false; }
      var q = qty();
      store.add({ variant_id: String(v.id), qty: q });
      addonLines(q).forEach(function (l) { store.add({ variant_id: l.id, qty: l.qty }); });
      publish('bz_add_to_cart', { variant_id: String(v.id), product_id: String(data.productId || ''), sku: v.sku || '', product_title: data.productTitle || data.shortName || '',
        product_handle: data.handle, qty: q, unit_price: v.price / 100, currency: currencyCode() });
      var line = store.find(String(v.id));
      els.confirm.innerHTML = '';
      els.confirm.appendChild(document.createTextNode('Added ' + q + ' ' + (v.title && v.title !== 'Default Title' ? '(' + v.title + ') ' : '')
        + 'to your cart' + (line && line.qty !== q ? ', now ' + line.qty + ' on the line' : '') + '. '));
      var link = document.createElement('a');
      link.href = data.projectUrl;
      link.textContent = 'View Cart';
      els.confirm.appendChild(link);
      return true;
    }

    /* lightbox */
    var lightbox = document.getElementById('pdp-lightbox');
    var lbStage = lightbox ? lightbox.querySelector('[data-lb-stage]') : null;
    var lbCaption = lightbox ? lightbox.querySelector('[data-lb-caption]') : null;
    var lbCount = lightbox ? lightbox.querySelector('[data-lb-count]') : null;
    var lbIndex = 0;
    function lbRender() {
      var list = slotsFor(shown.color);
      if (!list.length) { return; }
      lbIndex = (lbIndex + list.length) % list.length;
      var slot = list[lbIndex];
      var r = rec(shown.color, slot);
      lbStage.innerHTML = '<picture><img alt=""></picture>';
      lbStage.className = 'lightbox__stage' + (r.fit === 'contain' ? ' lightbox__stage--contain' : '');
      setImg(lbStage.querySelector('img'), r, '(max-width: 1200px) 96vw, 1160px');
      lbCaption.textContent = captionText(shown.color, slot, slot);
      lbCount.textContent = (lbIndex + 1) + ' of ' + list.length;
    }
    function openLightbox(opener) {
      if (!lightbox || !shown.color || !activeGallery().colors[shown.color]) { return; }
      lbIndex = Math.max(0, slotsFor(shown.color).indexOf(shown.slot));
      lbRender();
      openDialog(lightbox, opener);
    }
    if (lightbox) {
      lightbox.querySelector('[data-lb-prev]').addEventListener('click', function () { lbIndex -= 1; lbRender(); });
      lightbox.querySelector('[data-lb-next]').addEventListener('click', function () { lbIndex += 1; lbRender(); });
      lightbox.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { event.preventDefault(); lbIndex -= 1; lbRender(); }
        if (event.key === 'ArrowRight') { event.preventDefault(); lbIndex += 1; lbRender(); }
      });
    }

    function initTabs() {
      var tabs = document.querySelector('[data-tabs]');
      if (!tabs) { return; }
      var list = tabs.querySelector('[data-tablist]');
      var buttons = [].slice.call(list.querySelectorAll('[role="tab"]'));
      var panels = buttons.map(function (b) { return document.getElementById(b.getAttribute('aria-controls')); });
      function select(i, focus) {
        buttons.forEach(function (b, j) { b.setAttribute('aria-selected', i === j ? 'true' : 'false'); b.tabIndex = i === j ? 0 : -1; panels[j].hidden = i !== j; });
        if (focus) { buttons[i].focus(); }
      }
      buttons.forEach(function (b, i) {
        b.addEventListener('click', function () { select(i, false); });
        b.addEventListener('keydown', function (event) {
          var n = buttons.length;
          if (event.key === 'ArrowRight') { event.preventDefault(); select((i + 1) % n, true); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); select((i - 1 + n) % n, true); }
          if (event.key === 'Home') { event.preventDefault(); select(0, true); }
          if (event.key === 'End') { event.preventDefault(); select(n - 1, true); }
        });
      });
      list.hidden = false;
      tabs.classList.add('is-ready');
      panels.forEach(function (p) { p.setAttribute('tabindex', '0'); });
      select(0, false);
    }

    function renderAll(notes) { renderOptions(notes); renderPrice(); renderGallery(view.slot); renderDimensionFacts(); writeURL(); }

    root.addEventListener('change', function (event) {
      var input = event.target;
      if (!input.matches('input[data-option]')) { return; }
      var index = Number(input.getAttribute('data-option'));
      var value = input.value;
      var notes = null;
      selected[index] = value;
      if (!currentVariant()) {
        var before = selected.slice();
        before[index] = value;
        var moved = resolve(index, value);
        var shown = String(value).replace('In-Ground', 'In-ground');
        var onlyColor = colorIndex >= 0 && selected[colorIndex] !== before[colorIndex] &&
          selected.every(function (o, k) { return k === colorIndex || o === before[k]; });
        if (onlyColor) {
          var offered = [];
          data.variants.forEach(function (v) { if (v.opts[index] === value && offered.indexOf(v.opts[colorIndex]) < 0) { offered.push(v.opts[colorIndex]); } });
          notes = { index: index, text: shown + ' comes in ' + offered.join(', ').replace(/, ([^,]*)$/, ' or $1') + ', so ' + selected[colorIndex] + ' is selected.' };
        } else if (moved.length) { notes = { index: index, text: shown + ' is sold in another configuration: ' + moved.join(', ') + '.' }; }
      }
      renderAll(notes);
    });

    if (els.rail) {
      els.rail.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-slot]');
        if (!btn) { return; }
        view.slot = btn.getAttribute('data-slot');
        renderGallery(view.slot);
        writeURL();
        var again = els.rail.querySelector('[data-slot="' + view.slot + '"]');
        if (again) { again.focus(); }
      });
    }
    [].slice.call(root.querySelectorAll('[data-enlarge]')).forEach(function (btn) { btn.addEventListener('click', function () { openLightbox(btn); }); });

    root.addEventListener('click', function (event) {
      var step = event.target.closest('[data-qty-step]');
      if (!step) { return; }
      var base = parseQty(els.qty.value) || lastQty;
      lastQty = Math.min(999, Math.max(1, base + Number(step.getAttribute('data-qty-step'))));
      els.qty.value = String(lastQty);
      els.qtyError.textContent = '';
      els.qty.setAttribute('aria-invalid', 'false');
      renderPrice();
      writeURL();
    });
    els.qty.addEventListener('input', function () { if (onQtyInput()) { renderPrice(); writeURL(); } });
    /* commit once: a value that already equals the priced quantity keeps its message */
    function commitIfChanged() { if (els.qty.value !== String(lastQty)) { commitQty(); } }
    els.qty.addEventListener('change', commitIfChanged);
    els.qty.addEventListener('blur', commitIfChanged);

    els.add.addEventListener('click', addToProject);

    /* Get a Project Quote: this configuration at the quantity shown. An
       existing line is SET to that quantity, never added to. */
    function quoteThis(event, href) {
      if (parseQty(els.qty.value) === null) { event.preventDefault(); commitQty(); els.qty.focus(); return; }
      var v = currentVariant();
      if (!v) { return; }
      event.preventDefault();
      var id = String(v.id);
      if (store.find(id)) { store.setQty(id, qty()); } else { store.add({ variant_id: id, qty: qty() }); }
      addonLines(qty()).forEach(function (l) { if (store.find(l.id)) { store.setQty(l.id, l.qty); } else { store.add({ variant_id: l.id, qty: l.qty }); } });
      try { window.sessionStorage.setItem('bz-quote-from', 'pdp'); } catch (err) { /* ignore */ }
      window.location.href = href;
    }
    if (els.quote) {
      els.quote.addEventListener('click', function (event) { quoteThis(event, els.quote.getAttribute('href')); });
    }
    root.addEventListener('click', function (event) {
      var alt = event.target.closest('[data-quote-alt]');
      if (alt) { quoteThis(event, alt.getAttribute('href')); }
    });

    /* Buy Now: the permalink is kept current by renderBuy; blocked states do nothing */
    if (els.buyNow) {
      els.buyNow.addEventListener('click', function (event) {
        if (parseQty(els.qty.value) === null) { event.preventDefault(); commitQty(); els.qty.focus(); return; }
        if (els.buyNow.getAttribute('aria-disabled') === 'true') { event.preventDefault(); if (els.buyNote) { els.buyNote.focus && els.buyNote.focus(); } return; }
        try {
          if (window.BSS && typeof window.BSS.track === 'function') { window.BSS.track('bz_buy_now', { handle: data.handle, qty: qty() }); }
        } catch (err) { /* analytics never blocks */ }
        var bv = currentVariant();
        if (bv) {
          var bq = qty();
          publish('bz_buy_now', { lines: [{ variant_id: String(bv.id), product_id: String(data.productId || ''), sku: bv.sku || '', product_title: data.productTitle || data.shortName || '', qty: bq, unit_price: bv.price / 100 }],
            value: bv.price * bq / 100, currency: currencyCode(), lb: gramsToLb((Number(bv.grams) || 0) * bq) });
        }
      });
    }

    readURL();
    initTabs();
    var firstNote = null;
    if (urlNote) {
      firstNote = { index: urlNote.index, text: urlNote.value + ' is not offered on this model, so ' + selected[urlNote.index] + ' is selected.' };
    }
    renderAll(firstNote);
  }

  /* ======================================================================
     PROJECT: the cart and the quote request on one page.
     1 Items is the cart; Check Out sends it to Shopify checkout through a
     cart permalink when checkoutVerdict allows. Or 2 Delivery (ZIP ->
     state -> zone -> published band for the total crated weight, a
     planning estimate), 3 Contact, 4 Review and send: one native submit
     after validation; bz.js fills contact[Products and quantities] and
     contact[Project JSON] first.
     ====================================================================== */

  var ZIP_RANGES = [
    [5, 5, 'New York'], [6, 9, 'Puerto Rico'], [10, 27, 'Massachusetts'], [28, 29, 'Rhode Island'],
    [30, 38, 'New Hampshire'], [39, 49, 'Maine'], [50, 59, 'Vermont'], [60, 69, 'Connecticut'], [70, 89, 'New Jersey'],
    [100, 149, 'New York'], [150, 196, 'Pennsylvania'], [197, 199, 'Delaware'], [200, 200, 'District of Columbia'],
    [201, 201, 'Virginia'], [202, 205, 'District of Columbia'], [206, 219, 'Maryland'], [220, 246, 'Virginia'],
    [247, 268, 'West Virginia'], [270, 289, 'North Carolina'], [290, 299, 'South Carolina'], [300, 319, 'Georgia'],
    [320, 339, 'Florida'], [341, 349, 'Florida'], [350, 369, 'Alabama'], [370, 385, 'Tennessee'], [386, 397, 'Mississippi'],
    [398, 399, 'Georgia'], [400, 427, 'Kentucky'], [430, 459, 'Ohio'], [460, 479, 'Indiana'], [480, 499, 'Michigan'],
    [500, 528, 'Iowa'], [530, 549, 'Wisconsin'], [550, 567, 'Minnesota'], [570, 577, 'South Dakota'], [580, 588, 'North Dakota'],
    [590, 599, 'Montana'], [600, 629, 'Illinois'], [630, 658, 'Missouri'], [660, 679, 'Kansas'], [680, 693, 'Nebraska'],
    [700, 714, 'Louisiana'], [716, 729, 'Arkansas'], [730, 749, 'Oklahoma'], [750, 799, 'Texas'], [800, 816, 'Colorado'],
    [820, 831, 'Wyoming'], [832, 838, 'Idaho'], [840, 847, 'Utah'], [850, 865, 'Arizona'], [870, 884, 'New Mexico'],
    [885, 885, 'Texas'], [889, 898, 'Nevada'], [900, 961, 'California'], [967, 968, 'Hawaii'], [970, 979, 'Oregon'],
    [980, 994, 'Washington'], [995, 999, 'Alaska']
  ];

  /* -- freight: one calculation for the product page and the cart, from the same
        table the Shopify checkout uses (snippets/bz-data-freight). Crated items
        ride the LTL band for their total weight; lids and liners (tag
        freight:parcel) ship by parcel, a flat charge added once. ------------- */
  var ZIP_KEY = 'bz-zip-v1';
  function savedZip() { try { return window.localStorage.getItem(ZIP_KEY) || ''; } catch (err) { return ''; } }
  function saveZip(zip) { try { if (/^[0-9]{5}$/.test(zip)) { window.localStorage.setItem(ZIP_KEY, zip); } } catch (err) { /* ignore */ } }
  function zipState(zip) {
    if (!/^[0-9]{5}$/.test(zip)) { return null; }
    var p = Number(zip.slice(0, 3));
    for (var i = 0; i < ZIP_RANGES.length; i += 1) { if (p >= ZIP_RANGES[i][0] && p <= ZIP_RANGES[i][1]) { return ZIP_RANGES[i][2]; } }
    return null;
  }
  function freightZone(fr, state) {
    var n = state && fr ? (fr.state_to_zone || {})[state] : null;
    if (!n) { return null; }
    var zones = fr.zones || [];
    for (var i = 0; i < zones.length; i += 1) { if (Number(zones[i].zone) === Number(n)) { return zones[i]; } }
    return null;
  }
  function freightBand(zone, lb) {
    if (!zone || !lb) { return null; }
    for (var i = 0; i < zone.bands.length; i += 1) { if (lb <= Number(zone.bands[i].max_lb)) { return zone.bands[i]; } }
    return null;
  }
  /* status: nozip | unmapped (outside the zone map: Alaska, Hawaii, Puerto Rico) | over (above the table) | ok */
  function freightQuote(fr, zip, ltlLb, hasParcel) {
    var q = { zip: zip, state: zipState(zip), zone: null, band: null, ltlLb: ltlLb, ltlUsd: 0, parcelUsd: 0, totalUsd: 0, status: 'nozip' };
    if (!/^[0-9]{5}$/.test(zip)) { return q; }
    q.parcelUsd = hasParcel && fr && fr.parcel ? Number(fr.parcel.usd) || 0 : 0;
    if (!ltlLb) { q.status = 'ok'; q.totalUsd = q.parcelUsd; return q; }
    q.zone = freightZone(fr, q.state);
    if (!q.zone) { q.status = 'unmapped'; return q; }
    q.band = freightBand(q.zone, ltlLb);
    if (!q.band) { q.status = 'over'; return q; }
    q.ltlUsd = Number(q.band.usd) || 0;
    q.totalUsd = q.ltlUsd + q.parcelUsd;
    q.status = 'ok';
    return q;
  }
  function freightText(key, values) {
    var copy = readJSON('bz-freight-copy') || {};
    return String(copy[key] || '').replace(/__([a-z]+)__/g, function (all, name) {
      return values && values[name] !== undefined ? String(values[name]) : all;
    });
  }
  function moneyRange(low, high) { return money(low * 100) + (high > low ? '–' + money(high * 100) : ''); }
  function deliveryRange(fr, key) {
    var services = (fr && fr.services) || [], max = 0, service = null;
    services.forEach(function (s) {
      max = Math.max(max, Number(s.usd) || 0);
      if (s.key === key) { service = s; }
    });
    if (key === 'dock') { return { min: 0, max: 0, known: true, rate: fr.base_rate, service: null }; }
    if (service) { return { min: Number(service.usd) || 0, max: Number(service.usd) || 0, known: true, rate: service.rate, service: service }; }
    return { min: 0, max: max, known: false, rate: '', service: null };
  }
  function deliveryKey(siteType, unloadingValue) {
    if (LIMITED_SITES.indexOf(siteType) > -1) { return 'limited'; }
    if (siteType === 'Commercial property') {
      if (unloadingValue === 'Liftgate needed') { return 'liftgate'; }
      if (unloadingValue === 'Loading dock' || unloadingValue === 'Forklift or telehandler') { return 'dock'; }
    }
    return 'unknown';
  }
  function transitText(zone) { return zone ? String(zone.transit).replace('-', ' to ') + ' business days in transit' : ''; }
  /* free freight (Kai, 2026-10-12, D2; replaces the 2026-09-29 lower 48 + DC rule): orders of
     $10,000+ ship free ONLY to Texas, Oklahoma, Louisiana and Arkansas (fr.free_states, from
     snippets/bz-data-freight). Every other state pays freight, with no credit. */
  var FREE_STATES_DEFAULT = ['Texas', 'Oklahoma', 'Louisiana', 'Arkansas'];
  function freeStates(fr) {
    return fr && Array.isArray(fr.free_states) ? fr.free_states : FREE_STATES_DEFAULT;
  }
  function freeStatesText(fr) {
    var s = freeStates(fr);
    return s.length > 1 ? s.slice(0, -1).join(', ') + ' and ' + s[s.length - 1] : (s[0] || '');
  }
  function freeFreightState(fr, state) { return !!state && freeStates(fr).indexOf(state) > -1; }
  function freeFreightFor(fr, cents, q) {
    var min = fr && Number(fr.free_over_usd);
    return !!(min && cents >= min * 100 && q && q.zone && freeFreightState(fr, q.state));
  }
  /* an order over the threshold going to a state without free freight */
  function overFreeMinElsewhere(fr, cents, q) {
    var min = fr && Number(fr.free_over_usd);
    return !!(min && cents >= min * 100 && q && q.state && !freeFreightState(fr, q.state));
  }
  function freeFreightText(fr) {
    return 'Orders of ' + money(Number(fr.free_over_usd) * 100) + ' or more ship free to ' + freeStatesText(fr) + ', liftgate or limited-access delivery included.';
  }
  function freeElsewhereText(fr, place) {
    return ' Free freight on orders of ' + money(Number(fr.free_over_usd) * 100) + ' or more covers ' + freeStatesText(fr) + ' only, so freight to ' + place + ' is charged.';
  }
  function servicesText(fr) {
    var sv = (fr && fr.services) || [];
    if (!sv.length) { return ''; }
    var list = sv.map(function (x) { return x.label.toLowerCase() + ' +' + money(x.usd * 100); }).join(', ').replace(/, ([^,]*)$/, ' or $1');
    return fr.services_at_checkout
      ? 'Curbside to a dock or forklift. No dock or forklift, or a school, park, church, campground or home? Choose ' + list + ' at checkout.'
      : 'Curbside to a dock or forklift. If your site needs a liftgate, limited-access or residential delivery (' + list + '), request a project quote and we price them in.';
  }
  /* sites carriers bill as limited access (they include the liftgate) */
  var LIMITED_SITES = ['Park or trail', 'Campground or RV park', 'School or campus', 'Church', 'HOA or residential community', 'Municipal or government site', 'Residence', 'Job or construction site'];
  function serviceFor(fr, siteType, unloadingValue) {
    var sv = (fr && fr.services) || [];
    function get(k) { for (var i = 0; i < sv.length; i += 1) { if (sv[i].key === k) { return sv[i]; } } return null; }
    if (LIMITED_SITES.indexOf(siteType) > -1) { return get('limited'); }
    if (unloadingValue === 'Liftgate needed') { return get('liftgate'); }
    return null;
  }

  function initProject() {
    var DRAFT_KEY = 'bz-project-form-v1';
    var SENT_KEY = 'bz-project-sent-v1';
    /* v4.8: analytics and the Ads quote conversion wait for the success state. The submit
       handler stores {ga, value, currency, ref} here; the success page sends it once. */
    var PENDING_KEY = 'bz-quote-pending-v1';

    /* After a successful post Shopify reloads the page with the success state. */
    var done = document.getElementById('bz-quote-done');
    if (done) {
      var sent = null;
      try { sent = window.sessionStorage.getItem(SENT_KEY); window.sessionStorage.removeItem(SENT_KEY); } catch (err) { sent = null; }
      if (sent) {
        var wrap = done.querySelector('[data-sent-wrap]');
        var pre = done.querySelector('[data-sent]');
        if (wrap && pre) { pre.textContent = sent; wrap.hidden = false; }
        store.clear();
        try { window.localStorage.removeItem(DRAFT_KEY); } catch (err) { /* ignore */ }
      }
      /* read and remove first, so a reload of the success page never counts twice */
      var pending = null;
      try { pending = JSON.parse(window.sessionStorage.getItem(PENDING_KEY) || 'null'); window.sessionStorage.removeItem(PENDING_KEY); } catch (err) { pending = null; }
      if (pending && typeof pending === 'object') {
        if (pending.ga) { publish('bz_quote_submit', pending.ga); }
        try {
          if (window.BZAds && typeof window.BZAds.conversion === 'function') {
            window.BZAds.conversion('quote', { value: Number(pending.value) || 150, currency: pending.currency || 'USD', transaction_id: pending.ref || undefined });
          }
        } catch (err) { /* ads never block */ }
      }
      /* v4.8: only when this tab actually sent the form (not on a reload or a direct visit) */
      if (sent || pending) {
        try {
          if (window.BSS && typeof window.BSS.track === 'function') {
            window.BSS.track('bss_quote_form_submitted', { schema_version: '4.0', transport: 'contact_form' });
          }
        } catch (err) { /* analytics never blocks */ }
      }
      done.focus();
      return;
    }

    var form = document.getElementById('bz-quote-form');
    var wrapEl = form ? form.querySelector('[data-project]') : null;
    if (!form || !wrapEl) { return; }

    var catalog = readJSON('bz-catalog') || {};
    var thumbs = readJSON('bz-thumbs') || {};
    var freight = readJSON('bz-freight') || {};
    var coCfg = readJSON('bz-checkout') || { on: false, maxLb: 700 };
    var coBox = form.querySelector('[data-checkout-box]');
    var coNote = form.querySelector('[data-checkout-note]');
    var coSideNote = form.querySelector('[data-checkout-side-note]');
    var coLinks = [].slice.call(form.querySelectorAll('[data-checkout]'));

    var $ = function (sel) { return form.querySelector(sel); };
    var linesEl = $('[data-lines]');
    var emptyEl = $('[data-empty]');
    var undoEl = $('[data-undo]');
    var droppedEl = $('[data-dropped]');
    var itemsError = $('[data-items-error]');
    var reviewEl = $('[data-review]');
    var summaryErr = $('[data-error-summary]');
    var productsEl = $('[data-products]');
    var manualEl = $('[data-manual]');
    var manualHint = $('[data-manual-hint]');
    var manualHelp = $('[data-manual-help]');
    var jsonEl = $('[data-project-json]');
    var sendBtn = $('[data-send]');
    var qtyErrors = {};
    var lastGenerated = '';

    function field(name) { return form.querySelector('[data-f="' + name + '"]'); }
    function val(name) {
      var el = field(name);
      if (!el) { return ''; }
      if (el.type === 'checkbox') { return el.checked; }
      return String(el.value || '').trim();
    }
    function unloading() {
      var r = form.querySelector('input[name="contact[Unloading]"]:checked');
      return r ? r.value : 'Not sure';
    }
    function unloadingLabel() {
      var r = form.querySelector('input[name="contact[Unloading]"]:checked');
      return r ? r.parentNode.querySelector('span').textContent : 'Not sure';
    }

    /* drop saved ids the catalog does not carry, with a visible note */
    var dropped = store.prune(function (id) { return !!catalog[String(id)]; });
    if (dropped) {
      droppedEl.textContent = dropped === 1
        ? 'One saved item is no longer available. Add it again from its product page.'
        : dropped + ' saved items are no longer available. Add them again from their product pages.';
      droppedEl.hidden = false;
    }

    /* quantity ladders count every unit on the same ladder in the cart, the way the
       Shopify automatic discounts do at checkout */
    var GROUP_NAMES = { t: 'picnic tables and slatted receptacles', c: 'metal trash cans', b: 'benches and mesh cans' };
    function pools(raw) {
      var pool = {};
      raw.forEach(function (it) { var v = catalog[String(it.variant_id)]; if (v && v.q) { pool[v.q] = (pool[v.q] || 0) + it.qty; } });
      return pool;
    }
    function line(item, pool) {
      var v = catalog[String(item.variant_id)];
      if (!v) { return null; }
      var ladder = v.l || [];
      var group = v.q || '';
      var poolQty = group && pool && pool[group] ? pool[group] : item.qty;
      var unit = unitPrice(ladder, v.p, poolQty);
      var th = thumbs[v.h] || null;
      var file = th ? (th.c[v.c] || th.c[th.b] || null) : null;
      return {
        id: String(item.variant_id), qty: item.qty, handle: v.h, title: v.t, variant_title: v.v, sku: v.s, product_id: v.i ? String(v.i) : '',
        base: v.p, ladder: ladder, unit: unit, total: unit * item.qty, grams: Number(v.g) || 0, x: Number(v.x) || 0, parcel: !!v.k,
        addon: !!v.a, receptacle: !!v.r,
        group: group, poolQty: poolQty,
        url: '/products/' + encodeURIComponent(v.h) + '?variant=' + encodeURIComponent(String(item.variant_id)),
        image: file ? assetUrl(file + '.webp') : null
      };
    }
    function lines() {
      var raw = store.items();
      var pool = pools(raw);
      return raw.map(function (it) { return line(it, pool); }).filter(Boolean);
    }

    function totals(list) {
      var t = { lines: 0, units: 0, cents: 0, grams: 0, unknown: false, ltlGrams: 0, ltlUnknown: false, parcel: false, crated: false, quoteOnly: false };
      list.forEach(function (l) {
        t.lines += 1;
        t.units += l.qty;
        t.cents += l.total;
        if (l.x) { t.quoteOnly = true; }
        if (l.grams > 0) { t.grams += l.grams * l.qty; } else { t.unknown = true; }
        if (l.parcel) { t.parcel = true; return; }
        t.crated = true;
        if (l.grams > 0) { t.ltlGrams += l.grams * l.qty; } else { t.ltlUnknown = true; }
      });
      t.lb = t.unknown ? null : gramsToLb(t.grams);
      t.ltlLb = t.ltlUnknown ? null : gramsToLb(t.ltlGrams);
      t.ltlLbX = t.ltlUnknown ? null : gramsToLbExact(t.ltlGrams);
      return t;
    }
    function weightText(t) { return t.lb === null || !t.lines ? 'Confirmed in your quote' : lbShow(gramsToLbExact(t.grams), bandLimits(freight, coCfg.maxLb || 700)); }

    /* v4.8 Ads quote conversion value = expected contribution (ads plan v2.1 section 4.3):
       list subtotal x margin band x 25 % close rate, floor $25, cap $600; $150 when the request
       has no priced lines. Bands: under $3,000 28 %; $3,000 to $9,999 23 %; $10,000 or more
       5 % when every line is a picnic table (free freight eats the margin), else 11 %.
       Change the bands here when the free-freight rule changes; re-derive from the ledger
       after 10 quotes. D2 (2026-10-12, free freight only to TX, OK, LA and AR): the bands are
       deliberately left as they were; changing the Ads conversion value needs Kai's OK. */
    function quoteValue(list, t) {
      var usd = t && t.cents > 0 ? t.cents / 100 : 0;
      if (!list.length || !usd) { return 150; }
      var tablesOnly = list.every(function (l) { return /picnic-table/.test(l.handle || ''); });
      var band = usd < 3000 ? 0.28 : (usd < 10000 ? 0.23 : (tablesOnly ? 0.05 : 0.11));
      var v = usd * band * 0.25;
      return Math.round(Math.min(600, Math.max(25, v)) * 100) / 100;
    }
    /* quote reference, sent with the request as contact[Quote ref] and to Ads as transaction_id */
    function quoteRef() {
      var d = new Date();
      var ymd = String(d.getFullYear()).slice(2) + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
      var rnd = '';
      try { var a = new Uint32Array(2); window.crypto.getRandomValues(a); rnd = a[0].toString(36) + a[1].toString(36); } catch (err) { rnd = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2); }
      rnd = (rnd.toUpperCase().replace(/[^0-9A-Z]/g, '') + '000000').slice(0, 6);
      return 'BZQ-' + ymd + '-' + rnd;
    }

    function nextRung(l) {
      for (var i = 0; i < l.ladder.length; i += 1) { if (l.poolQty < l.ladder[i][0]) { return l.ladder[i]; } }
      return null;
    }

    /* -- freight estimate ------------------------------------------------ */

    function freightInfo(t) {
      var zip = val('zip').replace(/[^0-9]/g, '');
      var info = { zip: zip, state: null, zone: null, band: null, text: '', short: 'Add a ZIP code', figure: '', quote: null };
      if (zip.length !== 5) {
        info.figure = 'Enter the delivery ZIP code';
        info.text = 'The freight for your ZIP and total crated weight appears here, from the same table the checkout uses.';
        return info;
      }
      saveZip(zip);
      var q = freightQuote(freight, zip, t.ltlLbX || 0, t.parcel);
      info.quote = q;
      info.state = q.state;
      info.zone = q.zone || freightZone(freight, q.state);
      var place = info.state || 'ZIP ' + zip;
      var parcelNote = t.parcel && freight.parcel ? ' Lids and liners ship separately by parcel, ' + money(Number(freight.parcel.usd) * 100) + '.' : '';
      if (!t.lines) {
        info.figure = info.zone ? 'Zone ' + info.zone.zone + ', ' + info.zone.label : 'Outside the zone map';
        info.short = info.zone ? 'Zone ' + info.zone.zone : 'Confirmed in your quote';
        info.text = 'ZIP ' + zip + (info.state ? ' is in ' + info.state : '') + '. Add items to see the freight for your weight.';
        return info;
      }
      if (t.crated && t.ltlLb === null) {
        info.figure = 'Confirmed in your quote';
        info.short = 'Confirmed in your quote';
        info.text = 'One or more items have no published crated weight, so the freight is confirmed in your quote.';
        return info;
      }
      if (q.status === 'unmapped') {
        info.figure = 'Confirmed in your quote';
        info.short = 'Confirmed in your quote';
        info.text = place + ' is outside the lower 48 states and DC that online freight covers, so freight is quoted for your lane.' + parcelNote;
        return info;
      }
      if (t.quoteOnly) {
        info.free = freeFreightFor(freight, t.cents, q);
        info.figure = info.free ? 'Free' : 'Confirmed in your quote';
        info.short = info.figure;
        info.text = freightText(info.free ? 'quote_only_free' : 'quote_only') + (info.free ? ' ' + freeFreightText(freight) : '');
        return info;
      }
      if (freeFreightFor(freight, t.cents, q)) {
        info.free = true;
        info.figure = 'Free';
        info.short = 'Free';
        info.text = 'Free freight to ' + place + '. ' + freeFreightText(freight) + (q.status === 'over' ? ' At ' + lbShow(t.ltlLbX, bandLimits(freight, coCfg.maxLb || 700)) + ' this order is placed through a project quote.' : '') + ' ' + transitText(q.zone || info.zone) + '.';
        return info;
      }
      if (q.status === 'over') {
        info.figure = 'Confirmed in your quote';
        info.short = 'Confirmed in your quote';
        info.text = 'At ' + lbShow(t.ltlLbX, bandLimits(freight, coCfg.maxLb || 700)) + ' of crated freight the shipment is above the ' + lbText(coCfg.maxLb || 700) + ' the online table covers, so freight to ' + place + ' is confirmed in your quote. ' + transitText(info.zone) + '.'
          + (overFreeMinElsewhere(freight, t.cents, q) ? freeElsewhereText(freight, place) : '');
        return info;
      }
      info.band = q.band;
      var lift = unloading() === 'Liftgate needed' && freight.liftgate_usd && !freight.services_at_checkout ? freight.liftgate_usd : null;
      var delivery = deliveryRange(freight, q.ltlUsd ? deliveryKey(val('site_type'), unloading()) : 'dock');
      var svc = q.ltlUsd && freight.services_at_checkout ? delivery.service : null;
      info.service = svc;
      info.delivery = delivery;
      var total = q.totalUsd + delivery.min;
      var parts = [];
      if (q.ltlUsd) { parts.push(money(q.ltlUsd * 100) + ' freight'); }
      if (svc) { parts.push(money(svc.usd * 100) + ' ' + svc.label.toLowerCase()); }
      if (q.ltlUsd && q.parcelUsd) { parts.push(money(q.parcelUsd * 100) + ' parcel'); }
      info.figure = moneyRange(total, q.totalUsd + delivery.max) + (delivery.known && parts.length > 1 ? ' (' + parts.join(' + ') + ')' : '');
      info.short = moneyRange(total, q.totalUsd + delivery.max);
      info.text = (q.ltlUsd ? 'Freight to ' + place + ', Zone ' + q.zone.zone + ', for ' + lbShow(t.ltlLbX, bandLimits(freight, coCfg.maxLb || 700)) + ' crated, curbside. ' + transitText(q.zone) + '.' : 'Parcel to ' + place + '.')
        + (q.ltlUsd && q.parcelUsd ? ' Lids and liners ship separately by parcel.' : '')
        + (q.ltlUsd ? ' ' + (delivery.known ? freightText('choose_rate', { rate: delivery.rate }) : freightText('unknown_service') + ' ' + freightText('choose_matching')) : '')
        + ' ' + freightText('current_checkout')
        + (lift ? ' A liftgate is about ' + money(lift * 100) + ' more and is priced in a project quote.' : '')
        + (overFreeMinElsewhere(freight, t.cents, q) ? freeElsewhereText(freight, place) : '');
      return info;
    }

    /* -- payload ---------------------------------------------------------- */

    function itemsText(list) {
      var out = [];
      list.forEach(function (l, i) {
        var eachLb = gramsToLb(l.grams);
        var lineLb = gramsToLb(l.grams * l.qty);
        out.push((i + 1) + '. ' + l.qty + ' x ' + l.title);
        out.push('   Variant: ' + l.variant_title + ' | SKU: ' + l.sku + ' | Variant ID: ' + l.id);
        out.push('   Unit price: ' + moneyCents(l.unit) + ' | Line total: ' + moneyCents(l.total)
          + ' | Crated: ' + (lineLb !== null ? lbText(lineLb) + ' (' + lbText(eachLb) + ' each)' : 'weight confirmed in quote'));
      });
      return out.join('\n');
    }

    function typedText() {
      var v = productsEl ? productsEl.value.trim() : '';
      return v && v !== lastGenerated.trim() ? v : '';
    }

    function payload(list, t, f, typed) {
      var out = ['PROJECT QUOTE REQUEST', '', 'ITEMS'];
      if (list.length) { out.push(itemsText(list)); } else { out.push('No priced lines. Typed by the buyer:'); }
      if (typed) {
        if (list.length) { out.push(''); out.push('ALSO TYPED BY THE BUYER (not priced)'); }
        out.push(typed);
      }
      out.push('');
      out.push('TOTALS');
      out.push('Lines: ' + t.lines + ' | Units: ' + t.units);
      out.push('List subtotal before freight and tax: ' + moneyCents(t.cents));
      out.push('Total crated weight: ' + (t.lines ? (t.lb === null ? 'confirmed in quote (one or more items have no published weight)' : lbText(t.lb)) : 'none'));
      out.push(freightText('freight_request') + (f.band ? f.figure + ' (zone ' + f.zone.zone + ', curbside)' : f.short));
      out.push('');
      out.push('DELIVERY');
      out.push('ZIP: ' + (f.zip || '') + (f.state ? ' (' + f.state + ')' : ''));
      out.push('City: ' + (val('city') || 'not given'));
      out.push('State: ' + (val('state') || 'not given'));
      out.push('Street address: ' + (val('address') || 'not given yet'));
      out.push('Site type: ' + (val('site_type') || 'not given'));
      out.push('Unloading: ' + unloading());
      out.push('Access notes: ' + (val('access_notes') || 'none'));
      out.push('');
      out.push('CONTACT');
      out.push('Name: ' + val('name'));
      out.push('Email: ' + val('email'));
      out.push('Organization: ' + val('organization'));
      out.push('Phone: ' + (val('phone') || 'not given'));
      out.push('Purchase order: ' + (val('po') ? 'yes' : 'no'));
      out.push('Tax exempt: ' + (val('tax_exempt') ? 'yes, certificate to follow' : 'no'));
      out.push('Notes: ' + (val('notes') || 'none'));
      return out.join('\n');
    }

    function projectJSON(list, t, f) {
      return JSON.stringify({
        v: 1,
        lines: list.map(function (l) {
          return { variant_id: l.id, handle: l.handle, sku: l.sku, variant_title: l.variant_title, qty: l.qty,
            unit_price_cents: l.unit, line_total_cents: l.total, grams_each: l.grams || null };
        }),
        subtotal_cents: t.cents,
        weight_lb: t.lines ? t.lb : null,
        zip: f.zip || null,
        zone: f.zone ? f.zone.zone : null,
        freight_band_usd: f.band ? f.band.usd : null
      });
    }

    /* -- render ----------------------------------------------------------- */

    function lineHTML(l, index) {
      var next = nextRung(l);
      var priceNote = l.unit < l.base ? money(l.unit) + ' each, quantity price (list ' + money(l.base) + ')' : money(l.unit) + ' each';
      var gname = GROUP_NAMES[l.group] || 'items like this';
      var ladderNote = !l.ladder.length ? ''
        : (next ? 'At ' + next[0] + ' or more ' + gname + ' in the cart: ' + money(next[1]) + ' each.' : 'Best quantity price applied.')
          + (l.poolQty > l.qty ? ' Counts all ' + l.poolQty + ' ' + gname + ' in your cart.' : '');
      var lineLb = gramsToLb(l.grams * l.qty);
      var eachLb = gramsToLb(l.grams);
      var weight = lineLb !== null ? lbText(lineLb) + ' crated' : 'Crated weight confirmed in your quote';
      var id = 'q-' + index;
      var err = qtyErrors[l.id] || '';
      return '<li class="line" data-key="' + esc(l.id) + '">'
        + '<a class="line__thumb" href="' + esc(l.url) + '" tabindex="-1" aria-hidden="true">'
        + (l.image ? '<img src="' + esc(l.image) + '" alt="" width="96" height="96" loading="lazy">' : '') + '</a>'
        + '<div class="line__info">'
        + '<a class="line__name" href="' + esc(l.url) + '">' + esc(l.title) + '</a>'
        + '<p class="line__variant">' + esc(l.variant_title === 'Default Title' ? '' : l.variant_title) + '</p>'
        + '<p class="line__meta">SKU <span class="model-code" translate="no">' + esc(l.sku) + '</span> <span class="line__sep" aria-hidden="true">|</span> Variant <span class="model-code" translate="no">' + esc(l.id) + '</span></p>'
        + '<p class="line__price num">' + esc(priceNote) + '</p>'
        + (ladderNote ? '<p class="line__hint">' + esc(ladderNote) + '</p>' : '')
        + '</div>'
        + '<div class="line__qty">'
        + '<label class="t-label" for="' + id + '">Quantity<span class="sr-only"> for ' + esc(l.title) + ', ' + esc(l.variant_title) + '</span></label>'
        + '<div class="qty"><button class="qty__btn" type="button" data-step="-1" aria-label="Decrease quantity of ' + esc(l.title) + '">&minus;</button>'
        + '<input class="qty__input" id="' + id + '" type="text" inputmode="numeric" pattern="[0-9]*" value="' + l.qty + '" data-qty autocomplete="off" aria-describedby="' + id + '-err"' + (err ? ' aria-invalid="true"' : '') + '>'
        + '<button class="qty__btn" type="button" data-step="1" aria-label="Increase quantity of ' + esc(l.title) + '">+</button></div>'
        + '<span class="field__error" id="' + id + '-err" data-qty-err role="status">' + esc(err) + '</span>'
        + '<button class="link-button line__remove" type="button" data-remove>Remove<span class="sr-only"> ' + esc(l.title) + ', ' + esc(l.variant_title) + '</span></button>'
        + '</div>'
        + '<div class="line__total"><p class="line__sum num">' + money(l.total) + '</p><p class="line__weight num">' + esc(weight)
        + (eachLb !== null && l.qty > 1 ? ', ' + esc(lbText(eachLb)) + ' each' : '') + '</p></div>'
        + '</li>';
    }

    function renderLines(list) {
      var active = document.activeElement;
      var activeRow = active && active.closest ? active.closest('.line') : null;
      var activeKey = activeRow ? activeRow.getAttribute('data-key') : null;
      var activeIsQty = !!(active && active.hasAttribute && active.hasAttribute('data-qty'));
      var activeStep = active && active.getAttribute ? active.getAttribute('data-step') : null;
      linesEl.innerHTML = list.map(lineHTML).join('');
      emptyEl.hidden = list.length > 0;
      $('[data-totals]').hidden = list.length === 0;
      if (activeKey) {
        var row = linesEl.querySelector('.line[data-key="' + activeKey + '"]');
        var target = row ? row.querySelector(activeIsQty ? '[data-qty]' : (activeStep ? '[data-step="' + activeStep + '"]' : '[data-qty]')) : null;
        if (target) { target.focus(); }
      }
    }

    /* the Products and quantities box: generated text while there are
       lines, the buyer's own typing when there are none */
    function renderProducts(list) {
      if (!productsEl) { return; }
      var gen = list.length ? itemsText(list) : '';
      var current = productsEl.value;
      if (list.length) {
        if (!current.trim() || current === lastGenerated) { productsEl.value = gen; }
        var typedByHand = productsEl.value !== gen;
        manualEl.classList.toggle('is-generated', !typedByHand);
        if (manualHint) { manualHint.textContent = typedByHand ? '(typed by you, sent with the list above)' : ''; }
        if (manualHelp) { manualHelp.textContent = typedByHand ? 'Your typed text is sent after the priced list.' : ''; }
      } else {
        if (current === lastGenerated) { productsEl.value = ''; }
        manualEl.classList.remove('is-generated');
        if (manualHint) { manualHint.textContent = '(type them here if you prefer)'; }
        if (manualHelp) { manualHelp.textContent = 'Typed lines are sent as you write them and priced in your written quote.'; }
      }
      lastGenerated = gen;
    }

    function renderTotals(list, t, f) {
      var units = t.units + (t.units === 1 ? ' unit' : ' units') + (t.lines > 1 ? ' on ' + t.lines + ' lines' : '');
      $('[data-t-count]').textContent = units;
      $('[data-t-weight]').textContent = weightText(t);
      $('[data-t-subtotal]').textContent = money(t.cents);
      $('[data-s-count]').textContent = t.lines ? units : '0';
      $('[data-s-weight]').textContent = t.lines ? weightText(t) : 'None yet';
      $('[data-s-subtotal]').textContent = money(t.cents);
      $('[data-s-freight]').textContent = f.short;
      $('[data-est-figure]').textContent = f.figure;
      $('[data-est-body]').textContent = f.text;
    }

    function renderReview(list, t, f) {
      var typed = typedText();
      var itemsLine = t.lines
        ? t.units + ' units on ' + t.lines + (t.lines === 1 ? ' line' : ' lines') + ', ' + money(t.cents) + ' before freight and tax' + (typed ? ', plus your typed lines' : '')
        : (typed ? 'Your typed products and quantities' : 'None yet');
      var rows = [
        ['Items', itemsLine],
        ['Delivery', (f.zip.length === 5 ? 'ZIP ' + f.zip + (f.state ? ', ' + f.state : '') : 'ZIP code missing') + (val('site_type') ? ', ' + val('site_type') : '') + ', ' + unloadingLabel()],
        ['Freight', f.short],
        ['Reply to', [val('name'), val('email'), val('organization')].filter(Boolean).join(', ') || 'Contact details missing']
      ];
      reviewEl.innerHTML = '<dl class="review__list">' + rows.map(function (r) {
        return '<div><dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd></div>';
      }).join('') + '</dl>';
    }

    /* Check Out: list prices at Shopify checkout, freight from its table */
    /* delivery details the buyer typed travel with the checkout into the order
       (cart permalink: checkout[shipping_address][zip], attributes[...]) */
    function checkoutQuery(f) {
      var p = [];
      function add(k, v) { if (v) { p.push(encodeURIComponent(k) + '=' + encodeURIComponent(v)); } }
      if (f && f.zip && f.zip.length === 5) {
        add('checkout[shipping_address][zip]', f.zip);
        add('checkout[shipping_address][country]', 'US');
        add('attributes[Delivery ZIP]', f.zip);
      }
      add('attributes[Site type]', val('site_type'));
      var u = unloading();
      if (u && u !== 'Not sure') { add('attributes[Unloading]', u); }
      add('attributes[Access notes]', val('access_notes').slice(0, 250));
      add('attributes[Ordered from]', 'Brazos cart');
      return p.join('&');
    }

    /* ONE way out per cart: Check Out when checkout can take the whole list,
       otherwise the project quote with the reason. A buyer who pays by PO or is
       tax exempt can still open the quote from a checkout-ready cart. */
    var quoteOpen = /^#(quote|step-contact)$/.test(window.location.hash || '');
    var quoteReason = form.querySelector('[data-quote-reason]');
    var quoteTitle = form.querySelector('[data-quote-title]');
    function renderCheckout(list,f){f||(f=freightInfo(totals(list)));var canCheckout=!!(coCfg.on&&coLinks.length),verdict=canCheckout?checkoutVerdict(list,coCfg.maxLb,f.quote):{ok:!1,reason:"",volume:!1},mix=totals(list);if(verdict.ok&&f.service&&mix.parcel&&mix.crated&&(verdict.ok=!1,verdict.reason="Online checkout cannot add "+f.service.label.toLowerCase()+" when replacement lids or liners ship in the same order. Remove the lids and liners to check out now and order them separately, or send this list as a quote and we price the delivery in."),list.some(function(l){return l.addon})&&!list.some(function(l){return l.receptacle})&&(verdict.ok=!1,verdict.reason="Lids and spare liners ship only with a receptacle, on the same freight delivery. Add a receptacle to this cart, or remove the lid or liner."),!verdict.ok&&f&&f.free&&verdict.reason&&(verdict.reason+=" Freight is free on this order."),verdict.ok){var cq=checkoutQuery(f);cq&&(verdict.url+="?"+cq)}var route=list.length?verdict.ok?"checkout":"quote":typedText()?"quote":"empty";wrapEl.setAttribute("data-route",route),wrapEl.classList.toggle("is-quote-open",route!=="checkout"||quoteOpen),coBox&&(coBox.hidden=!list.length,coBox.classList.toggle("is-blocked",!verdict.ok)),quoteTitle&&(quoteTitle.textContent=route==="checkout"?"Request a quote instead":"Your quote request"),quoteReason&&(quoteReason.textContent=route==="quote"&&list.length?verdict.reason||"Online checkout is not available for this list.":"");var groupSavings={},checkoutSavings=0,qualifyingGroups=0;list.forEach(function(l){var saved=(l.base-l.unit)*l.qty;if(saved>0&&l.group)groupSavings[l.group]=(groupSavings[l.group]||0)+saved});Object.keys(groupSavings).forEach(function(g){qualifyingGroups+=1,checkoutSavings=Math.max(checkoutSavings,groupSavings[g])});var checkoutCents=verdict.listCents-checkoutSavings,note="";list.length&&verdict.ok&&(note=verdict.volume&&freight.qty_at_checkout?"Quantity prices are applied automatically at checkout: "+money(checkoutCents)+" for these items before freight and tax.":"Checkout charges these prices: "+money(verdict.listCents)+" before freight and tax.",qualifyingGroups>1&&freight.qty_at_checkout&&(note+=" Your project quote item subtotal is "+money(verdict.quoteCents)+"; request a quote instead to apply quantity prices across all qualifying groups."),note+=f&&f.band?" Freight to your ZIP: "+f.short+", curbside. Tax is added at checkout.":" Add your delivery ZIP above to see the freight now; checkout calculates it with tax.",f&&f.service&&(note+=' At checkout, choose the shipping rate "'+f.service.rate+'".')),coNote&&(coNote.textContent=note);var svcEl=form.querySelector("[data-checkout-services]");svcEl&&(svcEl.textContent=list.length&&verdict.ok&&!(f&&f.service)?servicesText(freight):""),coSideNote&&(coSideNote.textContent=list.length&&!verdict.ok?verdict.reason:""),coLinks.forEach(function(a){a.setAttribute("href",verdict.ok?verdict.url:"#quote"),verdict.ok?a.removeAttribute("aria-disabled"):a.setAttribute("aria-disabled","true"),a.classList.toggle("is-disabled",!verdict.ok),a.classList.contains("checkout-box__btn")&&(a.hidden=!verdict.ok)})}function renderAll() {
      var list = lines();
      var t = totals(list);
      var f = freightInfo(t);
      renderLines(list);
      renderProducts(list);
      renderTotals(list, t, f);
      renderReview(list, t, f);
      renderCheckout(list, f);
    }

    function renderFormParts() {
      var list = lines();
      var t = totals(list);
      var f = freightInfo(t);
      renderTotals(list, t, f);
      renderReview(list, t, f);
      renderCheckout(list, f);
    }

    /* -- drafts (per viewer convenience only, never prices) --------------- */

    var DRAFT_FIELDS = ['zip', 'city', 'state', 'address', 'site_type', 'access_notes', 'name', 'email', 'organization', 'phone', 'notes'];
    function saveDraft() {
      var d = {};
      DRAFT_FIELDS.forEach(function (n) { var el = field(n); d[n] = el ? el.value : ''; });
      d.unloading = unloading();
      d.po = val('po');
      d.tax_exempt = val('tax_exempt');
      d.typed = typedText();
      try { window.localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch (err) { /* storage blocked */ }
    }
    function loadDraft() {
      var d = null;
      try { d = JSON.parse(window.localStorage.getItem(DRAFT_KEY) || 'null'); } catch (err) { d = null; }
      if (!d) { return; }
      DRAFT_FIELDS.forEach(function (n) {
        var el = field(n);
        if (el && typeof d[n] === 'string' && !(n === 'email' && el.value)) { el.value = d[n]; }
      });
      if (d.unloading) {
        [].slice.call(form.querySelectorAll('input[name="contact[Unloading]"]')).forEach(function (r) { r.checked = r.value === d.unloading; });
      }
      if (field('po')) { field('po').checked = !!d.po; }
      if (field('tax_exempt')) { field('tax_exempt').checked = !!d.tax_exempt; }
      if (productsEl && typeof d.typed === 'string' && d.typed) { productsEl.value = d.typed; }
    }

    /* -- validation ------------------------------------------------------- */

    function setError(name, message) {
      var wrap = form.querySelector('[data-field="' + name + '"]');
      if (!wrap) { return; }
      var input = wrap.querySelector('input, select, textarea');
      var err = wrap.querySelector('[data-error]');
      wrap.classList.toggle('is-invalid', !!message);
      if (input) { input.setAttribute('aria-invalid', message ? 'true' : 'false'); }
      if (err) { err.textContent = message || ''; }
    }
    function check(name) {
      var v = val(name);
      var msg = '';
      if (name === 'zip') {
        if (!v) { msg = 'Enter the 5 digit delivery ZIP code.'; }
        else if (!/^[0-9]{5}$/.test(v)) { msg = 'Enter all 5 digits of the ZIP code.'; }
      } else if (name === 'name') {
        if (!v) { msg = 'Enter the name we should reply to.'; }
      } else if (name === 'email') {
        if (!v) { msg = 'Enter an email address for the reply.'; }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { msg = 'Check the email address, for example name@organization.org.'; }
      } else if (name === 'organization') {
        if (!v) { msg = 'Enter the organization, agency or site owner.'; }
      }
      setError(name, msg);
      return msg;
    }
    var REQUIRED = ['zip', 'name', 'email', 'organization'];
    var LABEL = { zip: 'Delivery ZIP code', name: 'Name', email: 'Email', organization: 'Organization' };
    var touched = {};

    function validateAll(list) {
      var errors = [];
      if (!list.length && !typedText()) {
        itemsError.textContent = 'Add at least one product, or type the products and quantities.';
        errors.push({ id: 'items-error', text: 'Items: add at least one product, or type the products and quantities.' });
      } else {
        itemsError.textContent = '';
      }
      var badQty = linesEl.querySelector('[data-qty][aria-invalid="true"]');
      if (badQty) { errors.push({ id: badQty.id, text: 'Items: a quantity needs a whole number from 1 to 999.' }); }
      REQUIRED.forEach(function (n) {
        var msg = check(n);
        if (msg) { errors.push({ id: field(n).id, text: LABEL[n] + ': ' + msg }); }
      });
      return errors;
    }
    function showSummary(errors) {
      if (!errors.length) { summaryErr.hidden = true; summaryErr.innerHTML = ''; return; }
      summaryErr.hidden = false;
      summaryErr.innerHTML = '<p><strong>' + errors.length + (errors.length === 1 ? ' thing needs' : ' things need') + ' attention before sending:</strong></p><ul>'
        + errors.map(function (e) { return '<li><a class="inline-link" href="#' + esc(e.id) + '">' + esc(e.text) + '</a></li>'; }).join('') + '</ul>';
    }

    /* -- events: lines ---------------------------------------------------- */

    var lastRemoved = null;

    linesEl.addEventListener('click', function (event) {
      var row = event.target.closest('.line');
      if (!row) { return; }
      var id = row.getAttribute('data-key');
      var list = store.items();
      var index = -1;
      for (var i = 0; i < list.length; i += 1) { if (list[i].variant_id === id) { index = i; } }
      if (index < 0) { return; }
      var item = list[index];
      var step = event.target.closest('[data-step]');
      if (step) {
        delete qtyErrors[id];
        store.setQty(id, Math.min(999, Math.max(1, item.qty + Number(step.getAttribute('data-step')))));
        return;
      }
      if (event.target.closest('[data-remove]')) {
        var l = line(item);
        lastRemoved = { item: { variant_id: item.variant_id, qty: item.qty }, index: index };
        delete qtyErrors[id];
        store.remove(id);
        undoEl.innerHTML = '<p class="proj-undo__text">Removed ' + esc(l ? l.qty + ' x ' + l.title + (l.variant_title !== 'Default Title' ? ', ' + l.variant_title : '') : 'an item') + '.</p>'
          + '<button class="btn btn--quiet btn--small" type="button" data-undo-btn>Undo</button>';
        var btn = undoEl.querySelector('[data-undo-btn]');
        if (btn) { btn.focus(); }
      }
    });

    undoEl.addEventListener('click', function (event) {
      if (!event.target.closest('[data-undo-btn]') || !lastRemoved) { return; }
      var r = lastRemoved;
      lastRemoved = null;
      undoEl.innerHTML = '';
      store.insert(r.item, r.index);
      var q = linesEl.querySelector('.line[data-key="' + r.item.variant_id + '"] [data-qty]');
      if (q) { q.focus(); }
    });

    linesEl.addEventListener('input', function (event) {
      var input = event.target.closest('[data-qty]');
      if (!input) { return; }
      var row = input.closest('.line');
      var id = row.getAttribute('data-key');
      var q = parseQty(input.value);
      var err = row.querySelector('[data-qty-err]');
      if (q === null) {
        input.setAttribute('aria-invalid', 'true');
        if (err) { err.textContent = 'Enter a whole number from 1 to 999.'; }
        return;
      }
      input.setAttribute('aria-invalid', 'false');
      if (err) { err.textContent = ''; }
      delete qtyErrors[id];
      store.setQty(id, q);
    });

    /* on commit the field always shows the quantity that is priced */
    function commitLineQty(event) {
      var input = event.target.closest ? event.target.closest('[data-qty]') : null;
      if (!input) { return; }
      var row = input.closest('.line');
      if (!row) { return; }
      var id = row.getAttribute('data-key');
      var item = store.find(id);
      if (!item) { return; }
      var q = parseQty(input.value);
      if (q === null) {
        qtyErrors[id] = 'Enter a whole number from 1 to 999. The quantity stays at ' + item.qty + '.';
        renderAll();
      } else if (String(q) !== input.value) {
        input.value = String(q);
      }
    }
    linesEl.addEventListener('change', commitLineQty);
    linesEl.addEventListener('focusout', commitLineQty);

    /* -- events: form ----------------------------------------------------- */

    form.addEventListener('input', function (event) {
      var t = event.target;
      if (t.closest('.line')) { return; }
      if (t.getAttribute('data-f') === 'zip') {
        var cleaned = t.value.replace(/[^0-9]/g, '').slice(0, 5);
        if (cleaned !== t.value) { t.value = cleaned; }
      }
      var name = t.getAttribute('data-f');
      if (name && touched[name]) { check(name); }
      if (t === productsEl) { renderProducts(lines()); }
      renderFormParts();
      saveDraft();
    });
    form.addEventListener('change', function (event) {
      if (event.target.closest('.line')) { return; }
      renderFormParts();
      saveDraft();
    });
    form.addEventListener('focusout', function (event) {
      var name = event.target.getAttribute ? event.target.getAttribute('data-f') : null;
      if (name && REQUIRED.indexOf(name) > -1 && event.target.value) { touched[name] = true; check(name); }
    });

    var sending = false;
    form.addEventListener('submit', function (event) {
      if (sending) { event.preventDefault(); return; }
      REQUIRED.forEach(function (n) { touched[n] = true; });
      var list = lines();
      var errors = validateAll(list);
      showSummary(errors);
      if (errors.length) {
        event.preventDefault();
        var first = document.getElementById(errors[0].id);
        if (first) { first.focus(); }
        return;
      }
      var t = totals(list);
      var f = freightInfo(t);
      var typed = typedText();
      var text = payload(list, t, f, typed);
      productsEl.value = text;
      lastGenerated = text;
      jsonEl.value = projectJSON(list, t, f);
      try { window.sessionStorage.setItem(SENT_KEY, text); } catch (err) { /* ignore */ }
      var fromPdp = false;
      try { fromPdp = window.sessionStorage.getItem('bz-quote-from') === 'pdp'; window.sessionStorage.removeItem('bz-quote-from'); } catch (err) { fromPdp = false; }
      /* v4.8: bz_quote_submit (same name and payload) and the Ads conversion are sent from the
         success state only (top of initProject), never here: the post can still fail. */
      var ref = quoteRef();
      var refEl = form.querySelector('input[name="contact[Quote ref]"]');
      if (!refEl) {
        refEl = document.createElement('input');
        refEl.type = 'hidden';
        refEl.name = 'contact[Quote ref]';
        form.appendChild(refEl);
      }
      refEl.value = ref;
      try {
        window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({
          ga: { lines_count: list.length, qty_total: t.units, value: t.cents / 100, currency: currencyCode(),
            zone: f.zone ? f.zone.zone : null, source: fromPdp ? 'pdp' : 'cart' },
          value: quoteValue(list, t), currency: currencyCode(), ref: ref
        }));
      } catch (err) { /* ignore */ }
      sending = true;
      window.setTimeout(function () { if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Sending'; } }, 0);
      /* Shopify's spam check (hCaptcha) can stall in some browsers without showing a challenge.
         After 20 s with no page change, re-arm the button and offer the email route. */
      window.setTimeout(function () {
        if (!sending) { return; }
        sending = false;
        if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = 'Send Quote Request'; }
        var slow = form.querySelector('[data-send-slow]');
        if (slow) { slow.hidden = false; }
      }, 20000);
      /* no preventDefault: the native contact form posts */
    });

    /* back navigation or a spam challenge page returns here: re-arm the button */
    window.addEventListener('pageshow', function () {
      sending = false;
      if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = 'Send Quote Request'; }
    });

    bus.on('project:change', function () {
      renderAll();
      if (lines().length) { itemsError.textContent = ''; }
    });

    function openQuote(event) {
      if (event) { event.preventDefault(); }
      quoteOpen = true;
      renderFormParts();
      var head = productsEl && productsEl.getClientRects().length ? productsEl : document.getElementById('step-items-title');
      var target = document.getElementById('quote');
      if (target) { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      if (head) { if (head !== productsEl) { head.setAttribute('tabindex', '-1'); } head.focus({ preventScroll: true }); }
    }
    form.addEventListener('click', function (event) {
      var opener = event.target.closest ? event.target.closest('[data-open-quote]') : null;
      if (opener) { openQuote(event); }
    });
    window.addEventListener('hashchange', function () {
      if (/^#(quote|step-contact)$/.test(window.location.hash || '')) { quoteOpen = true; renderFormParts(); }
    });

    coLinks.forEach(function (a) {
      a.addEventListener('click', function (event) {
        if (a.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          var target = document.getElementById('quote');
          if (target) { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
          return;
        }
        var bad = linesEl.querySelector('[data-qty][aria-invalid="true"]');
        if (bad) { event.preventDefault(); bad.focus(); return; }
        try {
          if (window.BSS && typeof window.BSS.track === 'function') { window.BSS.track('bz_cart_checkout', { lines: lines().length }); }
        } catch (err) { /* analytics never blocks */ }
        var cl = lines(), ct = totals(cl);
        publish('bz_checkout_click', { lines: cl.map(function (l) { return { variant_id: l.id, product_id: l.product_id, sku: l.sku || '', product_title: l.title, qty: l.qty, unit_price: l.base / 100 }; }),
          value: cl.reduce(function (sum, l) { return sum + l.base * l.qty; }, 0) / 100, currency: currencyCode(), lb: gramsToLb(ct.grams) });
      });
    });

    loadDraft();
    if (field('zip') && !field('zip').value && savedZip()) { field('zip').value = savedZip(); }
    renderAll();
    var serverErr = form.querySelector('[data-server-errors]');
    if (serverErr) { serverErr.focus(); }
  }
  /* Start last, so every module-level constant (SLOTS, ZIP_RANGES, ...) is defined
     before any init runs, even when the document is already parsed. */
  ready(function () {
    initMenu();
    initPlanningMenu();
    initSearch();
    initCardDots();
    markCurrentNav();
    renderChrome();
    initCollection();
    initProduct();
    initProject();
  });
}());
